import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import { collection, addDoc, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp, Timestamp, orderBy, limit } from 'firebase/firestore';
import { notifyOrderCreated } from '../services/NotificationSystem';
import { getOrCreateChatRoom } from '../utils/chatUtils';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ChevronLeft, ChevronRight, User, Handshake, RefreshCw, Component, Calendar, Palette, Image, FileText, CheckCircle, X, Plus, Trash2, Camera, Images, Edit2, FilePlus, ScanLine , Paperclip } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { getFeatures, isClinicScanIntakeOn, isClinicPatientsOn, isDeskReceptionOn } from '../utils/features';
// 🧾 「오늘 접수」 연결 (2026-10-03 대표 추가 지시) — 플래그 deskReception OFF면 버튼·연결 호출 전부 없음
import { listTodayReception, linkOrderToReception, isDeskClinicConsentConfirmedCached } from '../services/receptionApi';
import { receptionDeskError, kindLabel as receptionKindLabel, fmtHM as receptionHM, isDeskEntry as isDeskReceptionEntry, DESK_ADD_BADGE } from '../utils/deskReception';
// 치과 환자관리 1단계 (2026-09-10): [명부에서 선택] → 저장 후 linkOrderToPatient (workOrders에 직접 쓰지 않음 — 보안 P0-2)
import ClinicPatientPicker from './clinic/ClinicPatientPicker';
import { buildAppLinkCheck, isAppLinkCheckStale } from '../services/careAppApi';
import { linkOrderToPatient, matchRecordsByName, describeError as describePatientError, isAppRegisteredRecord, appRegisterBadgeLabel, sameNameCountMap, sameNameCountOf } from '../services/clinicPatientsApi';
import ProsthesisSelector from './ProsthesisSelector';
import { REMAKE_CAUSES, REMAKE_CAUSE_TEXT } from '../utils/remakeCauses';

function CreateOrder({ user }) {
    const navigate = useNavigate();
    const location = useLocation();
    // 일반 파일 첨부 (2026-08-12 대표 지시 — 사진 외 파일도 의뢰서에 동봉. 폴더는 브라우저 제약상 zip으로)
    const [attachFiles, setAttachFiles] = useState([]);   // [{file, name, size}]
    // 첨부 통합 (2026-08-21 대표 지시): 카메라/갤러리/파일 3버튼 → 1버튼.
    // 사진(file.type image/*)은 썸네일 미리보기(images), 그 외는 파일 목록(attachFiles)으로 자동 분류.
    // 어떤 파일을 골라도 같은 흐름이라 "버튼마다 동작이 다른" 혼란이 사라진다.
    const handleAttachUnified = (e) => {
        const picked = Array.from(e.target.files || []);
        const MAX = 200 * 1024 * 1024;
        const ok = picked.filter(f => f.size <= MAX);
        if (ok.length < picked.length) alert('200MB가 넘는 파일은 제외했습니다.');
        const imgs = ok.filter(f => (f.type || '').startsWith('image/'));
        const rest = ok.filter(f => !(f.type || '').startsWith('image/'));
        if (imgs.length) setImages(prev => [...prev, ...imgs.map(f => ({ file: f, uri: URL.createObjectURL(f) }))]);
        if (rest.length) setAttachFiles(prev => [...prev, ...rest.map(f => ({ file: f, name: f.name, size: f.size }))]);
        e.target.value = '';
    };

    // 편집 모드 관련 상태
    // 리메이크 작성: 지난 의뢰서를 열어 값만 가져오고 새 의뢰서로 저장한다 (편집이 아니다)
    const remakeFrom = location.state?.remakeFrom || null;
    const editMode = (location.state?.editMode || false) && !remakeFrom;
    const editOrderId = remakeFrom ? null : (location.state?.orderId || null);
    const editOrderData = remakeFrom || location.state?.orderData || null;

    const [patientName, setPatientName] = useState('');
    const [patientGender, setPatientGender] = useState('');
    const [patientAge, setPatientAge] = useState('');
    const [isInternalLab, setIsInternalLab] = useState(false);
    const [selectedPartnerId, setSelectedPartnerId] = useState('');
    const [partners, setPartners] = useState([]);
    // 납기 기본값 = 작성일+7일 (2026-09-29 대표 확정, 실측 75%가 6~10일 — 종전 작성 당일은 캘린더에 '거짓 급함'을 만들었다).
    // 편집·리메이크·스캔 연결(po.dueDate / editOrderData.dueDate) 복원 경로는 그대로 이 값을 덮어쓴다.
    const [dueDate, setDueDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 7); return d; });
    // 환자 내원일 (선택, 'YYYY-MM-DD' 문자열 — QR 공정체크 G, 2026-08-18). 비우면 저장하지 않는다.
    const [visitDate, setVisitDate] = useState(null);
    // 스캔 자동 기입 시 보철 선택기 리마운트용 (2026-08-18 대표 지시 "최대한 자동완성")
    // — 선택기는 마운트 시 1회만 그룹을 읽으므로, 그룹을 채운 뒤 key를 바꿔 다시 읽게 한다
    const [selectorEpoch, setSelectorEpoch] = useState(0);
    // 스캔 연결 시 자동 기입 (2026-08-18 대표 지시 — [의뢰서에 적용] 버튼 없이 즉시 기입).
    // 같은 스캔에 두 번 적용하지 않도록 스캔 id로 기억한다.
    const autoAppliedScanRef = useRef(new Set());
    const applyParsedOrderToForm = useCallback((po) => {
        if (!po || !(po.teethDetails || []).length) return;
        if (po.patientName) setPatientName(po.patientName);
        if (po.teeth?.length) setSelectedTeeth(po.teeth.map(String));
        // 치아 상세도 함께 구성 — 제출 items[]는 teethDetails에서 만들므로(그룹만 채우면
        // 선택기를 안 건드리고 제출 시 items가 비어 명세서 자동채움·실시간 매출이 0원 처리됨.
        // 편집 모드 복원(:367)과 같은 규약. 2026-08-18 검수 P1)
        const details = {};
        po.teethDetails.forEach(d => {
            const t = String(parseInt(d.tooth)); if (t === 'NaN') return;
            details[t] = {
                prosthesis: d.prosthesis || '',
                material: d.material || '',
                shade: d.shade || '',
                shadeType: '',
                implant: d.implant ? String(d.implant) : '',
                isPontic: !!d.isPontic || /pontic|폰틱/i.test(String(d.prosthesis || '')),
                isBridge: false,
            };
        });
        if (Object.keys(details).length) setTeethDetails(details);
        const shades = [...new Set(po.teethDetails.map(d => d.shade).filter(Boolean))];
        if (shades.length === 1) setSelectedShade(shades[0]);
        if (po.dueDate) { const d = new Date(po.dueDate); if (!isNaN(d)) setDueDate(d); }
        const summary = po.teethDetails.map(d =>
            `#${d.tooth} ${d.prosthesis}${d.material ? `(${d.material})` : ''}${d.shade ? ` ${d.shade}` : ''}`).join(', ');
        setSpecialNotes(prev => prev ? prev : `[3Shape 주문서] ${summary}`);
        // 보철 선택기 자동 채움 — 치아별 보철·재료·임플란트·폰틱. 브릿지 연결선만 수동(오연결 방지)
        try {
            const natMap = new Map(); const ponTeeth = []; const impMap = new Map();
            po.teethDetails.forEach(d => {
                const t = parseInt(d.tooth); if (isNaN(t)) return;
                if (d.implant) {
                    const b = String(d.implant);
                    if (!impMap.has(b)) impMap.set(b, []);
                    impMap.get(b).push(t);
                }
                if (d.isPontic || /pontic|폰틱/i.test(String(d.prosthesis || ''))) { ponTeeth.push(t); return; }
                const pros = d.prosthesis || (po.indications || [])[0] || '';
                if (!pros) return;
                const key = pros + '|' + (d.material || '');
                if (!natMap.has(key)) natMap.set(key, { teeth: [], prosthesis: pros, material: d.material || '' });
                natMap.get(key).teeth.push(t);
            });
            if (natMap.size || ponTeeth.length || impMap.size) {
                setProsthesisGroups([...natMap.values()]);
                setPonticGroups(ponTeeth.length ? [{ teeth: ponTeeth }] : []);
                setImplantGroups([...impMap.entries()].map(([brand, teeth]) => ({ teeth, brand })));
                setBridgeGroups([]);
                setSelectorEpoch(e => e + 1);   // 선택기 리마운트 → 새 그룹 읽기
            }
        } catch (e) { console.warn('보철 자동 채움 실패(치식만 적용):', e?.message); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [specialNotes, setSpecialNotes] = useState('');

    const [selectedTeeth, setSelectedTeeth] = useState([]);
    const [teethDetails, setTeethDetails] = useState({});

    const [prosthesisGroups, setProsthesisGroups] = useState([]);
    const [bridgeGroups, setBridgeGroups] = useState([]);
    const [ponticGroups, setPonticGroups] = useState([]);
    const [implantGroups, setImplantGroups] = useState([]);

    const [showPartnerModal, setShowPartnerModal] = useState(false);
    const [showShortcutModal, setShowShortcutModal] = useState(false);

    const [shadeType, setShadeType] = useState('classic');
    const [selectedShade, setSelectedShade] = useState('');

    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);

    const [isRemake, setIsRemake] = useState(false);
    const [remakeType, setRemakeType] = useState('');  // 'remake' | 'modify' | ''
    const [remakeKind, setRemakeKind] = useState('');  // 'remake'(새로 만듦) | 'repair'(기존 보철 수리) | '' — 2026-09-04 대표 승인
    const [remakeReason, setRemakeReason] = useState('');
    const [remakeCause, setRemakeCause] = useState('');   // 표준 사유 코드 (2026-08-09)
    // 원 의뢰서 연결 (2026-08-09 대표 확정) — 리메이크·수정을 고른 경우에만 지목한다
    const [remakeOf, setRemakeOf] = useState(null);       // { id, date, label }
    const [remakeCandidates, setRemakeCandidates] = useState([]);
    const [remakeSearching, setRemakeSearching] = useState(false);

    const [newShortcut, setNewShortcut] = useState('');
    const [specialNotesShortcuts, setSpecialNotesShortcuts] = useState([
        '급한 케이스', '재제작', '색상 확인 필요', '형태 주의', '교합 확인', '환자 요청사항',
    ]);
    const [shortcutsLoaded, setShortcutsLoaded] = useState(false);

    // 임시저장 복원 완료 플래그
    const [draftLoaded, setDraftLoaded] = useState(false);


    // 통합 에이전트 스캔 연동 (?scanId=)
    const scanIdParam = new URLSearchParams(location.search).get('scanId');
    const [linkedScan, setLinkedScan] = useState(null);
    // 스캔이 연결되고 주문서 파싱값이 있으면 즉시 자동 기입 (?scanId= 경로·제안 카드 연결 공통).
    // ⚠️ 이 effect는 반드시 linkedScan 선언 뒤에 있어야 한다 — 앞에 두면 의존성 배열이 TDZ로
    //    렌더 즉시 ReferenceError = 페이지 전체 흰 화면 (2026-08-18 배포 사고, 검수로 발견·수정).
    // 편집·리메이크 모드는 자동 기입 제외 — 복원된 기존 입력을 스캔 값이 소리 없이 덮는 사고 방지.
    useEffect(() => {
        const po = linkedScan?.parsedOrder;
        if (editMode || remakeFrom) return;
        if (!linkedScan?.id || !po || !(po.teethDetails || []).length) return;
        if (autoAppliedScanRef.current.has(linkedScan.id)) return;
        autoAppliedScanRef.current.add(linkedScan.id);
        applyParsedOrderToForm(po);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedScan]);

    // 🦷 치과 스캔 수집 숨김 (2026-08-08 대표 지시) — 기능 유지, 플래그 OFF면 스캔 제안·연결 UI 전부 비활성
    // 재개 = config/features.clinicScanIntake=true (배포 불필요)
    const [features, setFeatures] = useState(null);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatures(f); });
        return () => { alive = false; };
    }, []);
    // 판정: 플래그 && (베타 || stage 2 이상) — 2026-08-20 대표 확정 (베타 치과는 현행 유지)
    const scanIntakeOn = isClinicScanIntakeOn(features, user, user?.email);
    // 🦷 환자관리 [명부에서 선택] — 치과 계정 && (플래그 || 베타). 신규 작성일 때만(편집은 1단계 범위 밖)
    const isClinicAccount = ['clinic', 'dental', '치과'].includes(user?.businessType || user?.companyBusinessType);
    const clinicPatientsOn = isClinicAccount && isClinicPatientsOn(features, user, user?.email);
    const [showPatientPicker, setShowPatientPicker] = useState(false);
    const [linkedPatient, setLinkedPatient] = useState(null);   // { id, name, gender, birthYear } — 저장 후 CF로 연결

    // 🧾 「오늘 접수」 (2026-10-03 대표 추가 지시: "명부에서 선택을 안 하고 작성했으면 같은 환자이름이면 자동으로 해당 의뢰서로 저장, 그 당일만")
    //   - [오늘 접수] 버튼 → 오늘(KST) 접수 목록에서 고르면 환자명 채움 + receptionId 보관 → 저장 후 linkOrderToReception({orderId, receptionId})
    //   - 고르지 않고 이름만 쳐서 저장 → linkOrderToReception({orderId}) — 서버가 의뢰서 작성일과 같은 날 접수 중 이름 완전 일치 1명이면 자동 연결,
    //     2명 이상이면 candidates → 선택 카드, 0명이면 아무것도 안 함. 다른 날 접수와는 연결하지 않음(서버 판정)
    //   - 신규 작성만(편집 제외 — 명부 연결과 같은 범위). 연결 실패는 저장 완료 알림에 함께 표시(의뢰서 저장은 유지)
    const deskReceptionOn = isClinicAccount && isDeskReceptionOn(features);
    const [linkedReception, setLinkedReception] = useState(null);   // { id, name, kind, at }
    const [deskPickName, setDeskPickName] = useState('');   // [오늘 접수]에서 데스크 직접 추가 건을 골라 이름만 채운 경우 — 저장 시 자동 연결도 생략(10-04 보안 P2-1: 같은 이름 QR 환자 오연결 방지)
    const [receptionPicker, setReceptionPicker] = useState(null);   // null=닫힘 | { loading, list, err }
    const [receptionCandidates, setReceptionCandidates] = useState(null);   // 저장 후 동명 2명 이상: { orderId, list, busy, err }
    const openReceptionPicker = async () => {
        setReceptionPicker({ loading: true, list: [], err: '' });
        try {
            const r = await listTodayReception({ log: true, client: 'web' });   // 목록 열 때 열람 접속기록 1건
            setReceptionPicker({ loading: false, list: Array.isArray(r?.items) ? r.items : [], err: '' });
        } catch (e) {
            console.error('오늘 접수 목록 조회 실패:', e?.code || '', e?.details?.code || '');   // 환자명 기록 금지
            setReceptionPicker({ loading: false, list: [], err: receptionDeskError(e, '오늘 접수 목록을 불러오지 못했습니다.') });
        }
    };
    const pickReceptionCandidate = async (cand) => {
        const rc = receptionCandidates;
        if (!rc || rc.busy) return;
        setReceptionCandidates({ ...rc, busy: true, err: '' });
        try {
            await linkOrderToReception(rc.orderId, cand.id);
            setReceptionCandidates(null);
            navigate('/orders');
        } catch (e) {
            console.error('접수 연결(선택) 실패:', e?.code || '', e?.details?.code || '');
            setReceptionCandidates({ ...rc, busy: false, err: receptionDeskError(e, '연결하지 못했습니다. 다시 눌러 주세요.') });
        }
    };

    // 🦷 환자명 타이핑 → 명부 자동 제안 (환자앱 환자여정 P1 H1, 2026-09-21 대표 승인 "권고대로")
    //   스캔 제안(아래 scanSuggestion)과 같은 600ms 디바운스. 명부는 마운트당 1회만 list(patientRowsCacheRef) 후 메모리 필터.
    //   조건: 치과·플래그 && 신규 작성 && 미연결 && 2자 이상(법무 조건). [아니오] 누른 이름은 다시 제안하지 않는다.
    //   ⚠️ 환자명은 화면(치과 자기 명부)에만 — console 로그에 남기지 않는다.
    const patientRowsCacheRef = useRef({ rows: null });
    const [patientSuggest, setPatientSuggest] = useState([]);          // 일치 레코드 목록 (0=카드 없음)
    const [patientSuggestDismissed, setPatientSuggestDismissed] = useState('');   // [아니오] 누른 시점의 환자명
    useEffect(() => {
        if (!clinicPatientsOn || editMode || linkedPatient) { setPatientSuggest([]); return; }
        const name = patientName.trim();
        if (name.length < 2 || name === patientSuggestDismissed) { setPatientSuggest([]); return; }
        let alive = true;
        const t = setTimeout(async () => {
            try {
                const list = await matchRecordsByName(user?.companyId || user?.uid, name, { cache: patientRowsCacheRef.current });
                if (alive) setPatientSuggest(list);
            } catch (e) {
                // 제안은 보조 기능 — 실패해도 의뢰서 작성은 막지 않는다. 환자명은 기록하지 않음.
                console.error('명부 제안 조회 실패(제안 생략):', e?.code || '', e?.message || e);
                if (alive) setPatientSuggest([]);
            }
        }, 600);
        return () => { alive = false; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientName, linkedPatient, clinicPatientsOn, editMode, patientSuggestDismissed]);
    // 명부 레코드를 의뢰서에 걸기(제안 카드·픽커 공통). appLinkCheckAt = L-6 확인 창에서 [확인하고 연결]을 누른 시각(앱 등록 환자만)
    const applyLinkedRecord = (r, appLinkCheckAt = null) => {
        // 앱 등록 환자는 저장 직전 재확인(3시간 경과) 때 같은 확인 창을 다시 띄우려고 생년월일·끝 4자리도 메모리에만 들고 있다(저장하지 않음)
        setLinkedPatient({ id: r.id, name: r.name, gender: r.gender, birthYear: r.birthYear,
            ...(appLinkCheckAt ? { appLinkCheckAt, appReg: true, birthDate: r.birthDate || '', phoneLast4: r.phoneLast4 || '' } : {}) });
        setPatientName(r.name || '');
        if (r.gender) setPatientGender(r.gender);
        if (r.birthYear) setPatientAge(String(new Date().getFullYear() - r.birthYear));
    };
    // 🛡️ L-6 연결 확인 창 (덴클 케어 1차 W9, 정본 v2 §9 L-6 — 법적보안 강력 권고: pa-clinic 6항② 대조 의무 이행 증빙)
    //   앱 등록 환자를 고르면 바로 걸지 않고 이름·생년월일·휴대폰 끝 4자리를 보인 확인 창 → [확인하고 연결] 1회. 앱 등록이 아니면 기존대로 바로.
    //   기록: 저장 시 linkOrderToPatient 에 appLinkCheck(확인 시각 ms) 동봉 → 서버 접속기록(누가·언제) reason app_link_checked.
    //   서버 최종 계약(10-04): 값이 없거나 3시간이 지나면 연결 거부(app-link-check-required) → 저장 직전 재확인(handleSubmitInner)으로 예방.
    const [appLinkAsk, setAppLinkAsk] = useState(null);   // L-6 확인 창에 띄운 명부 레코드
    // 저장 직전 재확인(10-04 서버 계약 — 확인 시각 허용 창 3시간): 창을 다시 눌렀으면 같은 옵션으로 저장을 이어 간다.
    //   handleSubmit 은 렌더마다 새로 만들어지므로, 갱신된 linkedPatient 가 반영된 다음 렌더의 effect 에서 부른다(옛 클로저로 다시 묻는 것 방지)
    const [appLinkResubmit, setAppLinkResubmit] = useState(null);   // { skipImplantCheck } | null
    useEffect(() => {
        if (!appLinkResubmit) return;
        const opts = appLinkResubmit;
        setAppLinkResubmit(null);
        handleSubmit(opts);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appLinkResubmit]);
    const pickPatientRecord = (r) => {
        if (isAppRegisteredRecord(r)) { setAppLinkAsk(r); return; }
        applyLinkedRecord(r);
    };
    // 제안 카드 [연결] — 픽커 onSelect 와 같은 경로
    const applyPatientSuggestion = (r) => {
        pickPatientRecord(r);
        setPatientSuggest([]);
    };

    // 환자명 타이핑만으로 스캔 자동 제안 — 최근 스캔에서 진입하지 않아도 연결 (스캔토스 2026-07-25)
    const [scanSuggestion, setScanSuggestion] = useState(null);
    useEffect(() => {
        if (!scanIntakeOn) { setScanSuggestion(null); return; }   // 스캔 수집 숨김 시 조회 자체를 하지 않음
        if (linkedScan || !patientName || patientName.trim().length < 2) { setScanSuggestion(null); return; }
        const t = setTimeout(async () => {
            try {
                const clinicId = user?.companyId || user?.uid;
                const snap = await getDocs(query(collection(db, 'scans'),
                    where('clinicId', '==', clinicId), where('status', 'in', ['detected', 'ready'])));
                const name = patientName.trim();
                const tms = (v) => (v && typeof v.toMillis === 'function') ? v.toMillis() : (v ? new Date(v).getTime() || 0 : 0);
                const cand = snap.docs.map(d => ({ id: d.id, ...d.data() }))
                    .filter(x => !x.orderId && !x.dismissedAt)   // 「오늘」에서 숨긴 스캔은 제안하지 않는다 (09-03 점검 #8)
                    .filter(x => (x.patientNameGuess || '').includes(name) || name.includes(x.patientNameGuess || '~~'))
                    // 같은 환자 스캔이 여럿이면 실제 스캔 시각이 가장 최근인 것 (2026-08-17)
                    .sort((a, b) => tms(b.scannedAt || b.detectedAt) - tms(a.scannedAt || a.detectedAt))[0];
                setScanSuggestion(cand || null);
            } catch (e) { console.error('스캔 제안 조회 실패(제안 생략):', e?.message || e); setScanSuggestion(null); }
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientName, linkedScan, scanIntakeOn]);

    useEffect(() => {
        if (!scanIdParam || !scanIntakeOn) return;   // 스캔 수집 숨김 시 ?scanId= 직접 접근도 무시
        const loadScan = async () => {
            try {
                const scanDoc = await getDoc(doc(db, 'scans', scanIdParam));
                if (!scanDoc.exists()) { alert('스캔 정보를 찾을 수 없습니다.'); return; }
                const scanData = { id: scanDoc.id, ...scanDoc.data() };
                if (scanData.orderId) { alert('이미 의뢰서가 작성된 스캔입니다.'); return; }
                setLinkedScan(scanData);
                // 환자명 후보 프리필 (비어있을 때만)
                if (scanData.patientNameGuess) {
                    setPatientName(prev => prev || scanData.patientNameGuess);
                }
            } catch (e) {
                console.error('스캔 로드 실패:', e);
            }
        };
        loadScan();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanIdParam, scanIntakeOn]);

    // 커스텀 목록 상태
    const [prosthesisOptions, setProsthesisOptions] = useState([
        { label: 'Inlay', value: 'Inlay' },
        { label: 'Crown', value: 'Crown' },
        { label: 'POST', value: 'POST' },
        { label: 'Denture', value: 'Denture' },
        { label: '교정', value: '교정' },
        { label: '장치', value: '장치' },
    ]);
    const [prosthesisMaterialMap, setProsthesisMaterialMap] = useState({
        'Inlay': ['레진', '골드', '세라믹', '지르코니아', '하이브리드레진', '기타'],
        'Crown': ['지르코니아', 'PFM', 'PFZ', '메탈', '골드', '세라믹', '기타'],
        'POST': ['메탈', '지르코니아', '기타'],
        'Denture': ['CDO', 'RPD', 'VALPLAST', 'Temp Denture', 'Wire Temporary', '기타'],
        '교정': ['교정장치', '유지장치', '기타'],
        '장치': ['마우스가드', '이갈이장치', '기타'],
    });
    const [implantOptions, setImplantOptions] = useState([
        { label: 'Osstem TS Mini', value: 'Osstem TS Mini' },
        { label: 'Osstem TS Regular', value: 'Osstem TS Regular' },
        { label: 'Osstem TS Wide', value: 'Osstem TS Wide' },
        { label: 'Osstem KS', value: 'Osstem KS' }
    ]);
    const [customProsthesisOptions, setCustomProsthesisOptions] = useState([]);
    const [customImplantOptions, setCustomImplantOptions] = useState([]);
    const [customMaterialMap, setCustomMaterialMap] = useState({});
    // 보철 선택 최소 클릭 (2026-09-29 대표 승인): 조합 칩(크라운+임플란트 브랜드)
    const [customImplantCombos, setCustomImplantCombos] = useState([]);   // users.customImplantCombos [{crown, brand}] (+ 저장 실패 시 localStorage 폴백)
    const [recentImplantCombos, setRecentImplantCombos] = useState([]);   // 최근 의뢰서 50건에서 같은 치아에 nat+imp 함께 붙은 조합, 빈도순
    const [impOnlyWarn, setImpOnlyWarn] = useState(null);                 // 발송 전 확인 박스 { teeth: [25, 26] } | null
    const selectorRef = useRef(null);
    const impOnlyWarnRef = useRef(null);
    const submittingRef = useRef(false);                                 // handleSubmit 재진입 가드 (await 구간 2중 제출 방지)
    const [comboSaveNotice, setComboSaveNotice] = useState('');          // 조합 저장이 브라우저 폴백으로 빠졌을 때 한 줄 안내

    const targetUserId = user?.companyId || user?.uid;
    // 직원 계정은 rules(users update 6번 절 hasOnly)에 customImplantCombos가 없어 저장이 거부된다 → 브라우저 폴백
    const COMBO_FALLBACK_KEY = `dcImplantCombos_${targetUserId || 'anon'}`;
    const readComboFallback = () => {
        try {
            const raw = localStorage.getItem(COMBO_FALLBACK_KEY);
            const arr = raw ? JSON.parse(raw) : [];
            return Array.isArray(arr) ? arr.filter(c => c && c.crown && c.brand) : [];
        } catch (e) {
            console.error('[의뢰서] 조합 칩 폴백 읽기 실패:', e?.message || e);
            return [];
        }
    };
    const writeComboFallback = (arr) => {
        try { localStorage.setItem(COMBO_FALLBACK_KEY, JSON.stringify(arr)); }
        catch (e) { console.error('[의뢰서] 조합 칩 폴백 저장 실패:', e?.message || e); }
    };
    const mergeCombos = (a, b) => {
        const seen = new Set();
        return [...(a || []), ...(b || [])].filter(c => {
            if (!c || !c.crown || !c.brand) return false;
            const k = `${c.crown}|||${c.brand}`;
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    };

    // 자동 조합: 최근 의뢰서 50건(fromUserId==본인, createdAt desc — 색인 있음) 1회 조회.
    // 같은 치아에 보철(prosthesisGroups)과 임플란트(implantGroups)가 함께 붙은 조합을 빈도순으로 센다. 환자명은 사용·기록하지 않는다(SDK 특성상 문서 전체가 내려온다).
    useEffect(() => {
        if (!targetUserId) return;
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDocs(query(
                    collection(db, 'workOrders'),
                    where('fromUserId', '==', targetUserId),
                    orderBy('createdAt', 'desc'),
                    limit(50)
                ));
                const counts = {};
                snap.docs.forEach(d => {
                    const o = d.data() || {};
                    const natByTooth = {};
                    (o.prosthesisGroups || []).forEach(g => {
                        if (!g || !g.prosthesis) return;
                        (g.teeth || []).forEach(t => { natByTooth[t] = { prosthesis: g.prosthesis, material: g.material || '' }; });
                    });
                    (o.implantGroups || []).forEach(g => {
                        if (!g || !g.brand) return;
                        (g.teeth || []).forEach(t => {
                            const nat = natByTooth[t];
                            if (!nat) return;
                            const k = `${nat.prosthesis}|||${nat.material}|||${g.brand}`;
                            if (!counts[k]) counts[k] = { prosthesis: nat.prosthesis, material: nat.material, brand: g.brand, count: 0 };
                            counts[k].count += 1;
                        });
                    });
                });
                if (cancelled) return;
                setRecentImplantCombos(Object.values(counts).sort((a, b) => b.count - a.count));
            } catch (e) {
                console.error('[의뢰서] 최근 조합 조회 실패:', e?.code || '', e?.message || e);
            }
        })();
        return () => { cancelled = true; };
    }, [targetUserId]);

    // 보철 내용이 바뀌면 이전 확인 박스는 무효
    useEffect(() => { setImpOnlyWarn(null); }, [teethDetails]);

    // 원 의뢰서 찾기 (2026-08-09) — 환자 이름으로 최근 6개월 내 '내가 보낸' 의뢰서를 뒤진다.
    // 재제작 건은 후보에서 뺀다(재제작의 재제작을 원본으로 고르면 기간 계산이 꼬인다).
    const searchOriginalOrders = async (nameInput) => {
        const key = (nameInput || '').trim();
        if (!key) { setRemakeCandidates([]); return; }
        setRemakeSearching(true);
        try {
            const uidNow = user?.companyId || user?.uid;
            const snap = await getDocs(query(collection(db, 'workOrders'), where('fromUserId', '==', uidNow)));
            const since = new Date();
            since.setMonth(since.getMonth() - 6);
            const toD = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
            const list = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(o => o.source !== 'agent_auto' && !o.isRemake)
                .filter(o => (o.patientName || '').includes(key))
                .map(o => ({ o, d: toD(o.createdAt) }))
                .filter(x => x.d && x.d >= since)
                .sort((a, b) => b.d - a.d)
                .slice(0, 8)
                .map(({ o, d }) => {
                    const teeth = (o.teethDetails || []).map(t => t.tooth).filter(Boolean);
                    const pros = (o.teethDetails || [])[0]?.prosthesis || (o.prosthesisGroups || [])[0]?.prosthesis || '';
                    const label = `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()} · ${o.patientName || '환자 미상'}`
                        + (teeth.length ? ` · #${teeth.slice(0, 4).join(',')}${teeth.length > 4 ? '…' : ''}` : '')
                        + (pros ? ` · ${pros}` : '');
                    return { id: o.id, date: d, label, orderNumber: o.orderNumber || '' };
                });
            setRemakeCandidates(list);
        } catch (e) {
            console.error('[의뢰서] 원 의뢰서 검색 실패:', e);
            setRemakeCandidates([]);
        } finally {
            setRemakeSearching(false);
        }
    };
    const internalLabName = (user?.businessName || user?.companyName || user?.name || "내") + " 기공실";

    // localStorage 키
    const DRAFT_KEY = `createOrder_draft_${targetUserId}`;

    // 의뢰서 이어쓰기(임시저장 자동 저장·복원) 제거 — 2026-07-25 사장 지시.
    // 항상 새 의뢰서로 시작. 남아있을 수 있는 과거 임시저장은 진입 시 정리.
    useEffect(() => {
        if (!targetUserId) return;
        try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* 무시 */ }
        setDraftLoaded(true);
    }, [targetUserId, DRAFT_KEY]);

    // 임시저장 삭제 함수
    const clearDraft = () => {
        localStorage.removeItem(DRAFT_KEY);
    };

    // Firestore 시각을 어떤 형태로 받아도 날짜로 바꾼다 (화면 이동 중 {seconds}로 바뀌는 경우 포함)
    const toDateSafe = (v) => {
        if (!v) return null;
        let d;
        if (typeof v.toDate === 'function') d = v.toDate();
        else if (typeof v.seconds === 'number') d = new Date(v.seconds * 1000);
        else if (typeof v._seconds === 'number') d = new Date(v._seconds * 1000);
        else d = new Date(v);
        return isNaN(d.getTime()) ? null : d;
    };

    // 편집 모드일 때 기존 데이터로 폼 초기화
    useEffect(() => {
        if ((editMode || remakeFrom) && editOrderData) {
            console.log(remakeFrom ? '🔁 리메이크 작성(값 불러오기):' : '📝 편집 모드 진입:', { editOrderId, editOrderData });

            try {
                // 기존 주문 데이터로 폼 초기화
                setPatientName(editOrderData.patientName || '');
                setPatientGender(editOrderData.patientGender || '');
                setPatientAge(editOrderData.patientAge ? String(editOrderData.patientAge) : '');
                setIsInternalLab(editOrderData.isInternalLab || false);
                setSelectedPartnerId(editOrderData.toUserId || '');

                // 납기일 처리 - Timestamp 안전하게 변환
                if (editOrderData.dueDate) {
                    let dueDateValue;
                    if (editOrderData.dueDate.toDate && typeof editOrderData.dueDate.toDate === 'function') {
                        dueDateValue = editOrderData.dueDate.toDate();
                    } else if (editOrderData.dueDate.seconds) {
                        // Firestore Timestamp 객체가 직렬화된 경우
                        dueDateValue = new Date(editOrderData.dueDate.seconds * 1000);
                    } else if (editOrderData.dueDate instanceof Date) {
                        dueDateValue = editOrderData.dueDate;
                    } else {
                        dueDateValue = new Date(editOrderData.dueDate);
                    }

                    if (!isNaN(dueDateValue.getTime()) && !remakeFrom) {
                        setDueDate(dueDateValue);   // 리메이크는 지난 납기를 끌고 오지 않는다
                    }
                }
                // 내원일 복원 (선택 필드 — 리메이크는 새로 받는다)
                if (editOrderData.visitDate && !remakeFrom) {
                    const vv = editOrderData.visitDate.toDate ? editOrderData.visitDate.toDate()
                        : editOrderData.visitDate.seconds ? new Date(editOrderData.visitDate.seconds * 1000)
                            : new Date(editOrderData.visitDate);
                    if (!isNaN(vv.getTime())) {
                        setVisitDate(`${vv.getFullYear()}-${String(vv.getMonth() + 1).padStart(2, '0')}-${String(vv.getDate()).padStart(2, '0')}`);
                    }
                }

                setIsRemake(editOrderData.isRemake || false);
                setRemakeType(editOrderData.remakeType || (editOrderData.isRemake ? 'remake' : ''));
                setRemakeReason(editOrderData.remakeReason || '');
                if (remakeFrom) {
                    // 지난 의뢰서에서 시작한 재제작 — 표시·원 의뢰 연결을 자동으로 채운다.
                    // 사유는 비워 둔다(고르는 사람이 정해야 한다). 치식·재료 복원은 아래에서 그대로 이어진다.
                    setIsRemake(true);
                    setRemakeType('remake');
                    // 재제작/수리 구분 (2026-09-04): 선택 모달에서 고른 값. 수리는 특이사항에 "수리 요청:" 접두를 기본으로 넣는다(편집 가능).
                    const kind = remakeFrom.remakeKind === 'repair' ? 'repair' : 'remake';
                    setRemakeKind(kind);
                    const prevNotes = editOrderData.specialNotes || '';
                    setSpecialNotes(kind === 'repair' && !prevNotes.startsWith('수리 요청:') ? `수리 요청: ${prevNotes}` : prevNotes);
                    setRemakeReason('');
                    setRemakeCause('');
                    const d = toDateSafe(editOrderData.createdAt);
                    setRemakeOf({
                        id: remakeFrom.id,
                        date: d,
                        label: `${d ? `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()} · ` : ''}${editOrderData.patientName || '환자 미상'}`,
                    });
                } else {
                    setSpecialNotes(editOrderData.specialNotes || '');
                    setRemakeKind(editOrderData.remakeKind || '');
                    setRemakeCause(editOrderData.remakeCause || '');
                    setRemakeOf(editOrderData.remakeOfOrderId
                        ? { id: editOrderData.remakeOfOrderId, date: editOrderData.remakeOfOrderDate || null, label: editOrderData.remakeOfSummary || '' }
                        : null);
                }

                // 선택된 치아 복원
                if (editOrderData.items && editOrderData.items.length > 0) {
                    const teeth = editOrderData.items.map(item => item.toothNumber);
                    setSelectedTeeth(teeth);

                    // 치아 상세 정보 복원
                    const details = {};
                    editOrderData.items.forEach(item => {
                        details[item.toothNumber] = {
                            prosthesis: item.prosthesis || '',
                            material: item.material || '',
                            shade: item.shade || '',
                            shadeType: item.shadeType || '',
                            implant: item.implant || '',
                            isPontic: item.isPontic || false,
                            isBridge: item.isBridge || false,
                        };
                    });
                    setTeethDetails(details);
                }

                // 그룹 정보 복원
                if (editOrderData.prosthesisGroups) setProsthesisGroups(editOrderData.prosthesisGroups);
                if (editOrderData.bridgeGroups) {
                    const bridges = editOrderData.bridgeGroups.map(g => g.teeth || g);
                    setBridgeGroups(bridges.map(teeth => ({ teeth })));
                }
                if (editOrderData.ponticGroups) {
                    const pontics = editOrderData.ponticGroups.map(g => g.teeth || g);
                    setPonticGroups(pontics.map(teeth => ({ teeth })));
                }
                if (editOrderData.implantGroups) setImplantGroups(editOrderData.implantGroups);

                // Shade 정보 복원
                if (editOrderData.items && editOrderData.items[0]) {
                    setSelectedShade(editOrderData.items[0].shade || '');
                    setShadeType(editOrderData.items[0].shadeType || 'classic');
                }

                console.log('✅ 편집 모드 데이터 복원 완료');
            } catch (error) {
                console.error('❌ 편집 모드 데이터 복원 실패:', error);
            }

            // 편집 모드에서는 임시저장 복원 스킵
            setDraftLoaded(true);
        }
    }, [editMode, editOrderData, editOrderId]);

    const shadeOptions = {
        classic: ['A1', 'A2', 'A3', 'A3.5', 'A4', 'B1', 'B2', 'B3', 'B4', 'C1', 'C2', 'C3', 'C4', 'D2', 'D3', 'D4'],
        '3d-master': ['0M1', '0M2', '0M3', '1M1', '1M2', '2L1.5', '2L2.5', '2M1', '2M2', '2M3', '3L1.5', '3L2.5', '3M1', '3M2', '3M3', '4L1.5', '4L2.5', '4M1', '4M2', '4M3', '5M1', '5M2', '5M3'],
    };

    useEffect(() => { fetchPartners(); }, [user]);

    useEffect(() => {
        const savedData = localStorage.getItem('orderFromTreatment');
        if (savedData) {
            try {
                const data = JSON.parse(savedData);
                if (data.fromTreatment) {
                    setPatientName(data.patientName || '');
                    if (data.selectedTeeth && data.selectedTeeth.length > 0) {
                        setSelectedTeeth(data.selectedTeeth);
                        const details = {};
                        data.selectedTeeth.forEach(tooth => {
                            details[tooth] = { prosthesis: data.prosthesis || '', material: data.material || '', shade: data.shade || '' };
                        });
                        setTeethDetails(details);
                        if (data.prosthesis) {
                            setProsthesisGroups([{ teeth: data.selectedTeeth, prosthesis: data.prosthesis, material: data.material || '' }]);
                        }
                    }
                    if (data.shade) setSelectedShade(data.shade);
                    localStorage.removeItem('orderFromTreatment');
                    alert('치료기록 정보가 자동으로 입력되었습니다. 거래처와 기타 항목을 입력해주세요.');
                }
            } catch (e) { console.error('치료기록 데이터 파싱 실패', e); }
        }
    }, []);

    useEffect(() => {
        if (selectedShade && selectedTeeth.length > 0) {
            applyShadeToAll();
        }
    }, [selectedShade, shadeType]);


    // 커스텀 보철물/임플란트 옵션 및 단축어 불러오기
    useEffect(() => {
        const loadCustomOptions = async () => {
            if (!user) return;
            const targetId = user.companyId || user.uid;
            try {
                const userDoc = await getDoc(doc(db, "users", targetId));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    // 저장된 기본 옵션 불러오기 (있으면 대체)
                    if (data.savedProsthesisOptions) setProsthesisOptions(data.savedProsthesisOptions);
                    if (data.savedImplantOptions) {
                        // 문자열 배열을 객체 배열로 변환
                        const implantOpts = data.savedImplantOptions.map(opt =>
                            typeof opt === 'string' ? { label: opt, value: opt } : opt
                        );
                        // 기본 임플란트 옵션이 항상 포함되도록 보장
                        const defaultImplants = [
                            { label: 'Osstem TS Mini', value: 'Osstem TS Mini' },
                            { label: 'Osstem TS Regular', value: 'Osstem TS Regular' },
                            { label: 'Osstem TS Wide', value: 'Osstem TS Wide' },
                            { label: 'Osstem KS', value: 'Osstem KS' }
                        ];
                        const existingValues = implantOpts.map(opt => opt.value || opt);
                        const missingDefaults = defaultImplants.filter(d => !existingValues.includes(d.value));
                        setImplantOptions([...missingDefaults, ...implantOpts]);
                    }
                    if (data.savedMaterialMap) setProsthesisMaterialMap(data.savedMaterialMap);

                    // 커스텀 보철물 옵션 불러오기 (중복 제거)
                    if (data.customProsthesisOptions && Array.isArray(data.customProsthesisOptions)) {
                        const prosthesisSet = new Set();
                        const uniqueProsthesisOptions = [];
                        data.customProsthesisOptions.forEach(opt => {
                            const optValue = typeof opt === 'string' ? opt : (opt.value || opt.label);
                            if (optValue && !prosthesisSet.has(optValue)) {
                                prosthesisSet.add(optValue);
                                uniqueProsthesisOptions.push({ label: optValue, value: optValue });
                            }
                        });
                        setCustomProsthesisOptions(uniqueProsthesisOptions);
                    }

                    // 커스텀 임플란트 옵션 불러오기 (중복 방지 - savedImplantOptions와 겹치지 않도록)
                    if (data.customImplantOptions && Array.isArray(data.customImplantOptions)) {
                        // 기본 임플란트 값들
                        const defaultImplantValues = new Set([
                            'Osstem TS Mini', 'Osstem TS Regular', 'Osstem TS Wide', 'Osstem KS'
                        ]);
                        // savedImplantOptions에 이미 포함된 값들 수집
                        const savedValues = new Set();
                        if (data.savedImplantOptions && Array.isArray(data.savedImplantOptions)) {
                            data.savedImplantOptions.forEach(opt => {
                                const val = typeof opt === 'string' ? opt : (opt.value || opt.label || opt.name || opt);
                                if (val) savedValues.add(val);
                            });
                        }

                        const implantSet = new Set();
                        const allCustomImplants = [];
                        data.customImplantOptions.forEach(opt => {
                            const optValue = typeof opt === 'string' ? opt : (opt.value || opt.label || opt.name);
                            // 기본값이나 이미 savedImplantOptions에 있는 값은 제외
                            if (optValue && !implantSet.has(optValue) && !defaultImplantValues.has(optValue) && !savedValues.has(optValue)) {
                                implantSet.add(optValue);
                                allCustomImplants.push({ label: optValue, value: optValue });
                            }
                        });
                        setCustomImplantOptions(allCustomImplants);
                    }
                    if (data.customMaterialMap) setCustomMaterialMap(data.customMaterialMap);
                    // 조합 칩 (2026-09-29): 서버 저장분 + 브라우저 폴백분 병합
                    const serverCombos = Array.isArray(data.customImplantCombos) ? data.customImplantCombos : [];
                    setCustomImplantCombos(mergeCombos(serverCombos, readComboFallback()));
                    // 단축어 불러오기
                    if (data.specialNotesShortcuts && data.specialNotesShortcuts.length > 0) {
                        setSpecialNotesShortcuts(data.specialNotesShortcuts);
                    }
                }
            } catch (error) {
                console.error("커스텀 옵션 로드 실패:", error);
            }
            setShortcutsLoaded(true);
        };
        loadCustomOptions();
    }, [user]);

    // 보철물 추가
    const handleAddProsthesis = async (name, materials) => {
        const targetId = user.companyId || user.uid;
        const materialsArr = materials.split(",").map(m => m.trim()).filter(m => m);
        const newOption = { label: name, value: name };
        const updatedOptions = [...customProsthesisOptions, newOption];
        const updatedMaterialMap = { ...customMaterialMap, [name]: materialsArr.length > 0 ? materialsArr : ["기타"] };
        try {
            await updateDoc(doc(db, "users", targetId), {
                customProsthesisOptions: updatedOptions,
                customMaterialMap: updatedMaterialMap
            });
            setCustomProsthesisOptions(updatedOptions);
            setCustomMaterialMap(updatedMaterialMap);
        } catch (error) {
            console.error("보철물 추가 실패:", error);
        }
    };

    // 보철물 삭제
    const handleDeleteProsthesis = async (value) => {
        const targetId = user.companyId || user.uid;
        const updatedOptions = customProsthesisOptions.filter(opt => opt.value !== value);
        const updatedMaterialMap = { ...customMaterialMap };
        delete updatedMaterialMap[value];
        try {
            await updateDoc(doc(db, "users", targetId), {
                customProsthesisOptions: updatedOptions,
                customMaterialMap: updatedMaterialMap
            });
            setCustomProsthesisOptions(updatedOptions);
            setCustomMaterialMap(updatedMaterialMap);
        } catch (error) {
            console.error("보철물 삭제 실패:", error);
        }
    };

    // 임플란트 추가 (웹/앱 모두 연동 - 중복 방지)
    const handleAddImplant = async (brand) => {
        const targetId = user.companyId || user.uid;

        // 이미 존재하는지 확인 (기본 옵션 + 커스텀 옵션)
        const existingValues = new Set([
            ...implantOptions.map(opt => opt.value || opt.label || opt),
            ...customImplantOptions.map(opt => opt.value || opt.label || opt)
        ]);

        if (existingValues.has(brand)) {
            console.log("이미 존재하는 임플란트:", brand);
            return;
        }

        const newOption = { label: brand, value: brand };
        const updatedOptions = [...customImplantOptions, newOption];

        try {
            await updateDoc(doc(db, "users", targetId), {
                customImplantOptions: updatedOptions
                // savedImplantOptions는 더 이상 사용하지 않음 (중복 방지)
            });
            setCustomImplantOptions(updatedOptions);
        } catch (error) {
            console.error("임플란트 추가 실패:", error);
        }
    };

    // 임플란트 삭제 (웹/앱 모두 연동)
    const handleDeleteImplant = async (brand) => {
        const targetId = user.companyId || user.uid;
        const updatedOptions = customImplantOptions.filter(opt => (opt.value || opt) !== brand);

        try {
            await updateDoc(doc(db, "users", targetId), {
                customImplantOptions: updatedOptions
                // savedImplantOptions는 더 이상 사용하지 않음 (중복 방지)
            });
            setCustomImplantOptions(updatedOptions);
        } catch (error) {
            console.error("임플란트 삭제 실패:", error);
        }
    };

    // 조합 칩 추가/삭제 (2026-09-29) — users.customImplantCombos. 저장 거부(직원 계정 등) 시 localStorage 폴백에 두고 기록.
    const handleAddImplantCombo = async (crown, brand) => {
        if (!crown || !brand) return;
        const targetId = user.companyId || user.uid;
        const updated = mergeCombos(customImplantCombos, [{ crown, brand }]);
        setCustomImplantCombos(updated);
        try {
            await updateDoc(doc(db, "users", targetId), { customImplantCombos: updated });
            setComboSaveNotice('');
        } catch (error) {
            console.error("조합 칩 저장 실패(브라우저에만 보관):", error?.code || '', error?.message || error);
            writeComboFallback(mergeCombos(readComboFallback(), [{ crown, brand }]));
            setComboSaveNotice('조합이 이 브라우저에만 저장되었습니다. 회사 계정으로 저장하려면 대표 계정으로 추가하세요.');
        }
    };
    const handleDeleteImplantCombo = async (crown, brand) => {
        const targetId = user.companyId || user.uid;
        const updated = customImplantCombos.filter(c => !(c.crown === crown && c.brand === brand));
        setCustomImplantCombos(updated);
        writeComboFallback(readComboFallback().filter(c => !(c.crown === crown && c.brand === brand)));
        try {
            await updateDoc(doc(db, "users", targetId), { customImplantCombos: updated });
        } catch (error) {
            console.error("조합 칩 삭제 저장 실패:", error?.code || '', error?.message || error);
        }
    };

    const fetchPartners = async () => {
        if (!user) return;
        try {
            const searchUserId = user.companyId || user.uid;
            const sentQuery = query(collection(db, 'connections'), where('requesterId', '==', searchUserId), where('status', '==', 'accepted'));
            const sentSnapshot = await getDocs(sentQuery);
            const receivedQuery = query(collection(db, 'connections'), where('receiverId', '==', searchUserId), where('status', '==', 'accepted'));
            const receivedSnapshot = await getDocs(receivedQuery);

            const partnersList = [];
            const partnerIds = new Set();

            for (const docSnapshot of sentSnapshot.docs) {
                const data = docSnapshot.data();
                const partnerId = data.receiverId;
                if (!partnerIds.has(partnerId)) {
                    partnerIds.add(partnerId);
                    const partnerDoc = await getDoc(doc(db, 'users', partnerId));
                    if (partnerDoc.exists()) {
                        const partnerData = partnerDoc.data();
                        partnersList.push({ id: partnerId, name: partnerData.businessName || partnerData.name || '업체명 없음' });
                    }
                }
            }

            for (const docSnapshot of receivedSnapshot.docs) {
                const data = docSnapshot.data();
                const partnerId = data.requesterId;
                if (!partnerIds.has(partnerId)) {
                    partnerIds.add(partnerId);
                    const partnerDoc = await getDoc(doc(db, 'users', partnerId));
                    if (partnerDoc.exists()) {
                        const partnerData = partnerDoc.data();
                        partnersList.push({ id: partnerId, name: partnerData.businessName || partnerData.name || '업체명 없음' });
                    }
                }
            }
            setPartners(partnersList);
        } catch (error) {
            console.error('거래처 로드 실패:', error);
        }
    };

    // 달력 헬퍼 함수
    const getCalendarDays = () => {
        const year = calendarDate.getFullYear();
        const month = calendarDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];
        for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
        for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
        return days;
    };

    const isDateDisabled = (date) => {
        if (!date) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const isSameDay = (d1, d2) => {
        if (!d1 || !d2) return false;
        return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    };

    const applyShadeToAll = () => {
        if (!selectedShade) return;
        const newDetails = { ...teethDetails };
        selectedTeeth.forEach(tooth => {
            newDetails[tooth] = { ...newDetails[tooth], shade: selectedShade, shadeType: shadeType };
        });
        setTeethDetails(newDetails);
    };

    // handleImageUpload 제거 — handleAttachUnified로 통합 (2026-08-21)

    const removeImage = (index) => {
        setImages(images.filter((_, i) => i !== index));
    };

    const addShortcut = async () => {
        if (!newShortcut.trim()) {
            alert('단축어를 입력해주세요.');
            return;
        }
        if (specialNotesShortcuts.includes(newShortcut.trim())) {
            alert('이미 존재하는 단축어입니다.');
            return;
        }
        const shortcutName = newShortcut.trim();
        const updatedShortcuts = [...specialNotesShortcuts, shortcutName];
        setSpecialNotesShortcuts(updatedShortcuts);
        setNewShortcut('');

        // Firebase에 저장
        try {
            const targetId = user.companyId || user.uid;
            await updateDoc(doc(db, "users", targetId), {
                specialNotesShortcuts: updatedShortcuts
            });
            alert(`"${shortcutName}" 단축어가 추가되었습니다.`);
        } catch (error) {
            console.error("단축어 저장 실패:", error);
            alert("단축어 저장에 실패했습니다.");
        }
    };

    const removeShortcut = async (index) => {
        if (!window.confirm(`"${specialNotesShortcuts[index]}" 단축어를 삭제하시겠습니까?`)) return;

        const updatedShortcuts = specialNotesShortcuts.filter((_, i) => i !== index);
        setSpecialNotesShortcuts(updatedShortcuts);

        // Firebase에 저장
        try {
            const targetId = user.companyId || user.uid;
            await updateDoc(doc(db, "users", targetId), {
                specialNotesShortcuts: updatedShortcuts
            });
        } catch (error) {
            console.error("단축어 삭제 실패:", error);
        }
    };

    // opts.skipImplantCheck: 확인 박스 [이대로 보내기]에서만 true (버튼 onClick은 이벤트 객체를 넘기므로 명시 비교)
    const handleSubmit = async (opts) => {
        const skipImplantCheck = !!(opts && opts.skipImplantCheck === true);
        if (uploading) return; // 중복 제출 방지
        if (submittingRef.current) return; // 명부 조회(await) 등 setUploading 이전 구간의 2중 제출 방지
        submittingRef.current = true;
        try {
            await handleSubmitInner(skipImplantCheck);
        } finally {
            submittingRef.current = false;
        }
    };
    const handleSubmitInner = async (skipImplantCheck) => {
        if (!patientName.trim()) { alert('환자명을 입력해주세요.'); return; }
        if (!selectedPartnerId) { alert('거래처를 선택해주세요.'); return; }
        // 치아/보철 선택 없이도 주문 생성 가능 — 단, 빈 의뢰서는 1회 확인 후 진행
        const hasProsthesis = prosthesisGroups.length > 0 || bridgeGroups.length > 0 ||
            ponticGroups.length > 0 || implantGroups.length > 0;
        if (!hasProsthesis) {
            if (!window.confirm('선택된 보철물이 없습니다. 이대로 의뢰서를 보낼까요?')) return;
        }
        // 싱글/브릿지 미정 (2026-11-02 대표): 치아 2개 이상인데 [모두 싱글]·[브릿지로 묶기] 중 하나를 안 했으면 막고 안내 상자로 데려간다
        if (selectorRef.current && selectorRef.current.bridgeDecisionNeeded && selectorRef.current.bridgeDecisionNeeded()) {
            if (selectorRef.current.showBridgeDecision) selectorRef.current.showBridgeDecision();
            return;
        }
        // 임플란트 브랜드만 있고 보철 종류가 없는 치아 (2026-09-29): 명세서에 임플란트 행만 남는 원인 — 화면 내 확인 박스로 1회 묻는다.
        // "이대로 보내기"는 허용(정말 임플란트만 의뢰하는 경우가 있음).
        if (!skipImplantCheck) {
            const impOnlyTeeth = Object.entries(teethDetails)
                .filter(([, d]) => d && d.implant && !d.prosthesis)
                .map(([t]) => parseInt(t)).sort((a, b) => a - b);
            if (impOnlyTeeth.length > 0) {
                setImpOnlyWarn({ teeth: impOnlyTeeth });
                setTimeout(() => {
                    if (impOnlyWarnRef.current && impOnlyWarnRef.current.scrollIntoView) impOnlyWarnRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 0);
                return;
            }
        }
        // 사유를 골랐으면 그 자체가 사유다 — 글까지 쓰라고 막지 않는다 (2026-08-11).
        // 단 '기타'는 무엇인지 알 수 없으니 한 줄은 받는다.
        if (isRemake && !remakeCause && !remakeReason.trim()) { alert(remakeType === 'modify' ? '수정 사유를 고르거나 입력해주세요.' : '리메이크 사유를 고르거나 입력해주세요.'); return; }
        if (isRemake && remakeCause === 'other' && !remakeReason.trim()) { alert('「기타」를 고르셨습니다. 어떤 이유인지 한 줄 적어주세요.'); return; }
        // 표준 사유는 분석(사유별 분포)의 근거라 한 번 짚고 넘어간다. 강제는 아님 — 급할 때 막지 않는다.
        if (isRemake && !remakeCause &&
            !window.confirm('사유 선택을 안 하셨습니다. 선택하면 나중에 재제작 원인 분석에 쓰입니다.\n이대로 보낼까요?')) return;

        // 🦷 명부 미연결 확인창 (P1 H1 D-c, 대표 확정): 명부에 같은 이름이 있는데 연결 없이 저장하려 할 때 1회 확인.
        //   명부 조회 실패는 저장을 막지 않는다(확인창 생략·기록).
        if (clinicPatientsOn && !editMode && !linkedPatient?.id) {
            let candidates = [];
            try {
                candidates = await matchRecordsByName(user?.companyId || user?.uid, patientName.trim(), { cache: patientRowsCacheRef.current });
            } catch (e) {
                console.error('명부 미연결 확인 조회 실패(확인창 생략):', e?.code || '', e?.message || e);
            }
            if (candidates.length > 0 &&
                !window.confirm('명부에 같은 이름의 환자가 있습니다.\n명부에 연결하지 않고 저장할까요?')) return;
        }

        // 🛡️ L-6 저장 직전 재확인(10-04 서버 계약 app-link-check-required): 앱 등록 환자 확인 창을 누른 지 오래됐으면(3시간 창 − 여유 30분)
        //   저장 전에 같은 확인 창을 다시 띄운다. [확인하고 연결] → 시각 갱신 후 저장 계속, [취소] → 저장하지 않음(연결은 유지)
        if (clinicPatientsOn && !editMode && linkedPatient?.id && linkedPatient.appReg && isAppLinkCheckStale(linkedPatient.appLinkCheckAt)) {
            setAppLinkAsk({ id: linkedPatient.id, name: linkedPatient.name, gender: linkedPatient.gender, birthYear: linkedPatient.birthYear,
                birthDate: linkedPatient.birthDate, phoneLast4: linkedPatient.phoneLast4, recheck: true, submitOpts: { skipImplantCheck } });
            return;
        }

        try {
            setUploading(true);

            // 새 이미지 업로드 (편집 모드에서도 새 이미지가 있으면 업로드)
            const newImageUrls = [];
            for (const image of images) {
                const storageRef = ref(storage, `workOrders/${user.uid}/${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`);
                await uploadBytes(storageRef, image.file);
                const url = await getDownloadURL(storageRef);
                newImageUrls.push(url);
            }

            // 일반 파일 업로드 → attachments 배열 (받는 쪽 첨부 탭이 읽는 형식)
            const fileAttachments = [];
            for (const af of attachFiles) {
                const safe = af.name.replace(/[^\w가-힣.\-]/g, '_');
                const fRef = ref(storage, `workOrders/${user.uid}/files/${Date.now()}_${safe}`);
                await uploadBytes(fRef, af.file);
                const url = await getDownloadURL(fRef);
                const ext = (af.name.split('.').pop() || '').toLowerCase();
                fileAttachments.push({
                    type: ext === 'zip' ? 'zip' : /^(jpg|jpeg|png|gif|webp)$/.test(ext) ? 'image' : 'file',
                    filename: af.name, name: af.name, url, size: af.size,
                });
            }

            // 편집 모드: 기존 이미지 + 새 이미지
            const imageUrls = editMode && editOrderData?.imageUrls
                ? [...editOrderData.imageUrls, ...newImageUrls]
                : newImageUrls;

            const partnerDoc = await getDoc(doc(db, 'users', selectedPartnerId));
            const partnerData = partnerDoc.data();
            const toUserName = partnerData?.businessName || partnerData?.name || '알 수 없음';

            const orderFromUserId = user.companyId || user.uid;
            const fromUserDoc = await getDoc(doc(db, 'users', orderFromUserId));
            const fromUserData = fromUserDoc.data();
            const fromUserName = fromUserData?.businessName || fromUserData?.name || '알 수 없음';

            const items = Object.entries(teethDetails).map(([tooth, details]) => ({
                toothNumber: parseInt(tooth),
                prosthesis: details.prosthesis || '',
                material: details.material || '',
                shade: details.shade || '',
                shadeType: details.shadeType || '',
                implant: details.implant || '',
                isPontic: details.isPontic || false,
                isBridge: details.isBridge || false,
            }));

            if (editMode && editOrderId) {
                // 편집 모드: 기존 주문 업데이트
                const updateData = {
                    toUserId: selectedPartnerId,
                    toUserName,
                    participants: [orderFromUserId, selectedPartnerId],
                    isInternalLab: isInternalLab,
                    internalLabName: isInternalLab ? internalLabName : null,
                    patientName: patientName.trim(),
                    patientGender: patientGender || '',
                    patientAge: patientAge ? parseInt(patientAge) : null,
                    dueDate: Timestamp.fromDate(dueDate),
                    ...(visitDate ? { visitDate: Timestamp.fromDate(new Date(visitDate + 'T00:00:00')) } : {}),
                    items,
                    prosthesisGroups: prosthesisGroups || [],
                    bridgeGroups: bridgeGroups || [],
                    ponticGroups: ponticGroups || [],
                    implantGroups: implantGroups || [],
                    specialNotes: specialNotes.trim(),
                    imageUrls,
                    ...(fileAttachments.length ? { attachments: [...(editMode && editOrderData?.attachments ? editOrderData.attachments : []), ...fileAttachments] } : {}),
                    isRemake,
                    remakeType: isRemake ? remakeType : '',
                    remakeKind: isRemake ? (remakeKind || '') : '',   // 'remake' | 'repair' | ''(구규약: 없음=재제작)
                    remakeReason: isRemake ? (remakeReason.trim() || REMAKE_CAUSE_TEXT[remakeCause] || '') : '',
                    remakeCause: isRemake ? remakeCause : '',
                    remakeOfOrderId: isRemake ? (remakeOf?.id || null) : null,
                    remakeOfOrderDate: isRemake ? (remakeOf?.date || null) : null,
                    remakeOfSummary: isRemake ? (remakeOf?.label || '') : '',
                    updatedAt: new Date(),
                    // 수정 정보 추가
                    isEdited: true,
                    editedAt: serverTimestamp(),
                    editedBy: orderFromUserId,
                    editedByName: fromUserName,
                    // 수신확인 초기화 (수정됐으니 다시 확인 필요)
                    readByReceiver: false,
                    readAt: null,
                };

                await updateDoc(doc(db, 'workOrders', editOrderId), updateData);

                // 주문 완료 시 임시저장 삭제
                clearDraft();

                alert('주문이 성공적으로 수정되었습니다!');
                navigate(`/view-order/${editOrderId}`);
            } else {
                // 새 주문 생성
                const today = new Date();
                const dateStr = today.toISOString().slice(0, 10).replace(/-/g, '');
                const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
                const orderNumber = `${dateStr}-${randomStr}`;

                const orderData = {
                    orderNumber, fromUserId: orderFromUserId, fromUserName, toUserId: selectedPartnerId, toUserName,
                    participants: [orderFromUserId, selectedPartnerId],
                    isInternalLab: isInternalLab,
                    internalLabName: isInternalLab ? internalLabName : null,
                    patientName: patientName.trim(), patientGender: patientGender || '', patientAge: patientAge ? parseInt(patientAge) : null,
                    dueDate: Timestamp.fromDate(dueDate),
                    ...(visitDate ? { visitDate: Timestamp.fromDate(new Date(visitDate + 'T00:00:00')) } : {}),
                    items,
                    prosthesisGroups: prosthesisGroups || [],
                    bridgeGroups: bridgeGroups || [],
                    ponticGroups: ponticGroups || [],
                    implantGroups: implantGroups || [],
                    specialNotes: specialNotes.trim(), imageUrls, ...(fileAttachments.length ? { attachments: fileAttachments } : {}), isRemake, remakeType: isRemake ? remakeType : '', remakeKind: isRemake ? (remakeKind || '') : '', remakeReason: isRemake ? (remakeReason.trim() || REMAKE_CAUSE_TEXT[remakeCause] || '') : '', remakeCause: isRemake ? remakeCause : '',
                    remakeOfOrderId: isRemake ? (remakeOf?.id || null) : null,
                    remakeOfOrderDate: isRemake ? (remakeOf?.date || null) : null,
                    remakeOfSummary: isRemake ? (remakeOf?.label || '') : '',
                    status: 'pending', createdAt: new Date(), updatedAt: new Date(),
                    // 디지털 파일 입력 UI 제거(2026-08-21) — 신규 주문은 항상 빈 값, 첨부는 attachments로 통합
                    digitalFiles: [],
                    designStatus: null,
                    // 통합 에이전트 스캔 연동
                    scanId: linkedScan?.id || null,
                };

                const orderRef = await addDoc(collection(db, 'workOrders'), orderData);

                // 스캔 문서에 주문 연결 + 업로드 요청 (에이전트가 감지해 파일 전송)
                if (linkedScan?.id) {
                    try {
                        const scanRef = doc(db, 'scans', linkedScan.id);
                        // 최신 상태를 다시 읽어 ready일 때만 ordered로 전환 — 페이지 로드 시점의
                        // stale status를 되쓰면 에이전트가 올린 'ready'가 회귀해 파일이 전달되지 않음
                        const freshSnap = await getDoc(scanRef);
                        const freshStatus = freshSnap.exists() ? freshSnap.data().status : null;
                        await updateDoc(scanRef, {
                            orderId: orderRef.id,
                            labId: selectedPartnerId,
                            uploadRequested: true,
                            updatedAt: new Date(),
                            ...(freshStatus === 'ready' ? { status: 'ordered' } : {}),
                        });
                    } catch (e) { console.error('스캔 연결 실패:', e); }
                }
                try { await notifyOrderCreated(selectedPartnerId, orderRef.id, orderNumber, fromUserName); } catch (e) { console.log('알림 발송 실패:', e); }
                if (!isInternalLab && orderFromUserId !== selectedPartnerId) {
                    try { await getOrCreateChatRoom(orderFromUserId, selectedPartnerId, 'system', orderRef.id); } catch (e) { console.log('채팅방 생성 실패:', e); }
                }

                // 🦷 환자 명부 연결 (환자관리 1단계) — 실패 시 화면에 알리되 의뢰서 저장은 그대로 유지
                let patientLinkNote = '';
                if (clinicPatientsOn && linkedPatient?.id) {
                    try {
                        await linkOrderToPatient(orderRef.id, linkedPatient.id, linkedPatient.appLinkCheckAt ? { appLinkCheck: buildAppLinkCheck(linkedPatient.appLinkCheckAt) } : undefined);
                        patientLinkNote = `\n환자 명부 연결: ${linkedPatient.name}`;
                    } catch (e) {
                        // app-link-check-required: 저장 직전 재확인을 거쳤는데도 서버 창(3시간)을 넘긴 경우(PC 시계 오차 등).
                        // 2026-10-17 점검 P1-1: 저장된 의뢰서 재연결 경로 신설 — 환자 상세 [의뢰서 연결] · 의뢰서 보기 [환자 연결](PatientOrderLink.js). 안내 문구를 실제 경로로 정정
                        patientLinkNote = `\n환자 명부 연결 실패: ${describePatientError(e)}\n(의뢰서는 저장되었고 환자 연결만 되지 않았습니다. 환자 관리 › 환자 상세의 [의뢰서 연결]에서 이 의뢰서를 골라 다시 연결해 주세요)`;
                    }
                }

                // 🧾 오늘 접수 연결 (플래그 deskReception) — 고른 접수가 있으면 그것, 없으면 서버 자동 판정(같은 날·이름 완전 일치 1명)
                let receptionNote = '';
                let receptionPending = null;
                // 호출 조건(10-04 검토 P2): [오늘 접수]에서 골랐거나, 그 치과의 처리위탁 확인이 끝난 경우만 —
                //   접수를 쓰지 않는(확인 안 한) 치과의 의뢰서 저장에는 호출도 실패 문구도 붙이지 않는다. 확인 여부는 세션 캐시
                const deskPicked = !!deskPickName && patientName.trim() === deskPickName;   // 데스크 직접 추가 건에서 이름만 불러옴 → 연결 호출 자체를 하지 않음
                let receptionUsable = !deskPicked && !!linkedReception?.id;
                if (deskReceptionOn && patientName.trim() && !receptionUsable && !deskPicked) {
                    try { receptionUsable = await isDeskClinicConsentConfirmedCached(); }
                    catch (e) { console.error('처리위탁 확인 조회 실패 — 자동 접수 연결 생략:', e?.code || '', e?.details?.code || ''); }
                }
                if (deskReceptionOn && patientName.trim() && receptionUsable) {
                    try {
                        const lr = await linkOrderToReception(orderRef.id, linkedReception?.id || null);
                        const cands = Array.isArray(lr?.candidates) ? lr.candidates : [];
                        if (lr?.linked === 'auto' || lr?.linked === 'picked') receptionNote = '\n오늘 접수와 연결됨';
                        else if (cands.length >= 2) receptionPending = { orderId: orderRef.id, list: cands, busy: false, err: '' };
                    } catch (e) {
                        console.error('접수 연결 실패:', e?.code || '', e?.details?.code || '');
                        receptionNote = `\n오늘 접수 연결 실패: ${receptionDeskError(e, '잠시 후 다시 시도해 주세요.')}\n(의뢰서는 저장되었습니다)`;
                    }
                }

                // 주문 완료 시 임시저장 삭제
                clearDraft();

                // 첨부 개수 병기 (2026-08-21): 첨부가 안 담긴 채 발송되면 여기서 바로 보인다
                const attachNote = (newImageUrls.length || fileAttachments.length)
                    ? `\n사진 ${newImageUrls.length}장 · 파일 ${fileAttachments.length}개 포함`
                    : '';
                alert(`주문이 성공적으로 생성되었습니다!\n주문번호: ${orderNumber}${attachNote}${patientLinkNote}${receptionNote}`);
                // 오늘 같은 이름 접수 2명 이상 — 이동하지 않고 선택 카드를 띄운다(고르거나 [연결하지 않음]을 누르면 목록으로)
                if (receptionPending) { setReceptionCandidates(receptionPending); return; }
                navigate('/orders');
            }
        } catch (error) {
            console.error(editMode ? '주문 수정 실패:' : '주문 생성 실패:', error);
            alert(editMode ? '주문 수정에 실패했습니다.' : '주문 생성에 실패했습니다.');
        } finally {
            setUploading(false);
        }
    };

    const selectedPartnerName = isInternalLab ? internalLabName : (partners.find(p => p.id === selectedPartnerId)?.name || '');
    const dDayCount = Math.max(0, Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24)));

    return (
        <div style={styles.container}>
            <PageHeader icon={FilePlus} title={editMode ? "주문 수정" : "주문 작성"} />
            {showPatientPicker && (
                <ClinicPatientPicker clinicId={user?.companyId || user?.uid} initialTerm={patientName}
                    onClose={() => setShowPatientPicker(false)}
                    onSelect={(r) => {
                        pickPatientRecord(r);
                        setShowPatientPicker(false);
                    }} />
            )}
            {/* 🛡️ 정본 v2 L-6 — 앱 등록 환자 연결 확인 창 */}
            {appLinkAsk && (
                <div role="dialog" aria-modal="true"
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 440, padding: '18px 18px 14px' }}>
                        <div style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.7 }}>
                            {`${appLinkAsk.name || '(이름 없음)'} 님은 덴클 케어 앱에 등록된 환자입니다. 연결하면 이 의뢰서의 보철물 제작 과정이 환자 앱에 표시됩니다. 생년월일 ${appLinkAsk.birthDate || (appLinkAsk.birthYear ? `${appLinkAsk.birthYear}년` : '(없음)')}·휴대폰 끝 4자리 ${appLinkAsk.phoneLast4 || '(없음)'}가 우리 환자 기록과 같은지 확인해 주세요.`}
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                            <button type="button" onClick={() => { if (appLinkAsk.recheck) alert('의뢰서를 보내지 않았습니다. 확인 후 다시 보내기를 눌러 주세요.'); setAppLinkAsk(null); }}
                                style={{ padding: '8px 14px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#475569', fontSize: 13, cursor: 'pointer' }}>취소</button>
                            <button type="button" onClick={() => {
                                const ask = appLinkAsk;
                                applyLinkedRecord(ask, Date.now());
                                setAppLinkAsk(null);
                                if (ask.recheck) setAppLinkResubmit(ask.submitOpts || { skipImplantCheck: false });   // 저장 직전 재확인 → 저장 계속
                            }}
                                style={{ padding: '8px 14px', border: 'none', borderRadius: 8, background: '#4F46E5', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>확인하고 연결</button>
                        </div>
                    </div>
                </div>
            )}
            {/* 🧾 오늘 접수 환자 선택 */}
            {receptionPicker && (
                <div role="dialog" aria-modal="true" onClick={() => setReceptionPicker(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 420, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
                            <b style={{ fontSize: 15, color: '#0f172a' }}>오늘 접수 환자</b>
                            <button type="button" onClick={() => setReceptionPicker(null)} style={{ marginLeft: 'auto', border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>닫기</button>
                        </div>
                        <div style={{ overflowY: 'auto', padding: 12 }}>
                            {receptionPicker.loading ? <div style={{ fontSize: 13, color: '#64748b', padding: 12, textAlign: 'center' }}>불러오는 중…</div>
                                : receptionPicker.err ? <div style={{ fontSize: 13, color: '#8a2020', background: '#fdecec', border: '1px solid #f5cfcf', borderRadius: 8, padding: '9px 12px' }}>{receptionPicker.err}</div>
                                : receptionPicker.list.length === 0 ? <div style={{ fontSize: 13, color: '#64748b', padding: 12, textAlign: 'center' }}>오늘 접수한 환자가 없습니다.</div>
                                : receptionPicker.list.map(it => {
                                    // 데스크 직접 추가 건(10-04) — 이름만 채우고 접수 연결은 하지 않는다(receptionId 보관 안 함 → 저장 후 linkOrderToReception 지정 대상 아님).
                                    //   처리위탁 1.2: 의뢰서 작성 때 이름 불러와 채우기만. 서버도 지정 연결 거부(reception-not-linkable)·자동 연결 제외
                                    const desk = isDeskReceptionEntry(it);
                                    return (
                                        <button key={it.id} type="button"
                                            onClick={() => {
                                                if (desk) { setLinkedReception(null); setDeskPickName((it.name || '').trim()); }
                                                else { setLinkedReception({ id: it.id, name: it.name, kind: it.kind, at: it.at }); setDeskPickName(''); }
                                                setPatientName(it.name || ''); if (linkedPatient && it.name !== linkedPatient.name) setLinkedPatient(null); setReceptionPicker(null);
                                            }}
                                            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 6, border: `1px solid ${!desk && linkedReception?.id === it.id ? '#0f6b5c' : '#e2e8f0'}`, borderRadius: 8, background: !desk && linkedReception?.id === it.id ? '#e2f0ec' : '#fff', cursor: 'pointer', flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{it.name}</span>
                                            {desk
                                                ? <span style={{ fontSize: 11, fontWeight: 600, padding: '0 8px', borderRadius: 999, background: '#fff', color: '#46524e', border: '1px solid #9aa6a2' }}>{DESK_ADD_BADGE}</span>
                                                : <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, background: it.kind === 'new' ? '#e8eaf8' : '#eceff0', color: it.kind === 'new' ? '#3f51b5' : '#5d6b66' }}>{receptionKindLabel(it.kind)}</span>}
                                            {desk && <span style={{ fontSize: 11.5, color: '#64748b' }}>이름만 채웁니다</span>}
                                            <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#64748b', fontFamily: 'ui-monospace, Menlo, monospace' }}>{receptionHM(it.at)}</span>
                                        </button>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            )}
            {/* 🧾 저장 후 오늘 같은 이름 접수 2명 이상 — 누구인지 고르기 */}
            {receptionCandidates && (
                <div role="dialog" aria-modal="true"
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 420, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
                            <b style={{ fontSize: 15, color: '#0f172a' }}>{`오늘 같은 이름 접수 ${receptionCandidates.list.length}명 — 누구인지 골라 주세요`}</b>
                            <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 4 }}>의뢰서는 저장되었습니다. 고른 접수에 이 의뢰서가 연결됩니다.</div>
                        </div>
                        <div style={{ overflowY: 'auto', padding: 12 }}>
                            {receptionCandidates.list.map(c => (
                                <button key={c.id} type="button" disabled={receptionCandidates.busy} onClick={() => pickReceptionCandidate(c)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 6, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>
                                    <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{c.name}</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, background: c.kind === 'new' ? '#e8eaf8' : '#eceff0', color: c.kind === 'new' ? '#3f51b5' : '#5d6b66' }}>{receptionKindLabel(c.kind)}</span>
                                    <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#64748b', fontFamily: 'ui-monospace, Menlo, monospace' }}>{`${receptionHM(c.at)} 접수`}</span>
                                </button>
                            ))}
                            {receptionCandidates.err && <div style={{ fontSize: 13, color: '#8a2020', background: '#fdecec', border: '1px solid #f5cfcf', borderRadius: 8, padding: '9px 12px', marginBottom: 6 }}>{receptionCandidates.err}</div>}
                            <button type="button" disabled={receptionCandidates.busy} onClick={() => { setReceptionCandidates(null); navigate('/orders'); }}
                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#f8fafc', color: '#475569', fontSize: 13, cursor: 'pointer' }}>연결하지 않음</button>
                        </div>
                    </div>
                </div>
            )}
            <div style={styles.scrollView}>
                <div style={styles.contentWrapper}>
                    {/* 🦷 명부 자동 제안 카드 (P1 H1) — 1건: 바로 연결 / 2건 이상: 픽커로. 혜택·추천 표현 없음(사실 안내만) */}
                    {clinicPatientsOn && !editMode && !linkedPatient && patientSuggest.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: '10px', padding: '10px 14px', marginBottom: '12px' }}>
                            <User size={16} color="#4F46E5" />
                            <span style={{ fontSize: '13px', color: '#3730A3' }}>
                                {patientSuggest.length === 1 ? (
                                    <>명부에 <b>{patientSuggest[0].name}</b>{patientSuggest[0].birthDate ? `(${patientSuggest[0].birthDate})` : (patientSuggest[0].birthYear ? `(${patientSuggest[0].birthYear}년)` : '')} 환자가 있습니다 — 이 의뢰서를 연결할까요?</>
                                ) : (
                                    <>명부에 같은 이름의 환자가 <b>{patientSuggest.length}명</b> 있습니다 — 연결할 환자를 골라 주세요.</>
                                )}
                            </span>
                            {/* W9(덴클 케어 1차, 10-04 대표 결정 "앱 등록 즉시 성립" 보완): 앱 등록 배지·번호 끝 4자리·동명이인 주의 — 표시만, 추가 클릭 없음 */}
                            {patientSuggest.length === 1 && (() => {
                                const r0 = patientSuggest[0];
                                const dup = sameNameCountOf(sameNameCountMap(patientRowsCacheRef.current.rows), r0.name) >= 2;
                                return (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '12px' }}>
                                        {isAppRegisteredRecord(r0) && <span style={{ padding: '2px 8px', borderRadius: '999px', background: '#ECFEFF', color: '#0E7490', fontWeight: 700 }}>{appRegisterBadgeLabel(r0)}</span>}
                                        <span style={{ color: '#475569' }}>{r0.phoneLast4 ? `끝 4자리 ${r0.phoneLast4}` : '번호 없음'}</span>
                                        {dup && <span style={{ padding: '2px 8px', borderRadius: '999px', background: '#FFEDD5', color: '#9A3412', fontWeight: 700 }}>동명이인 주의 — 명부에서 생년월일·번호를 확인해 주세요</span>}
                                    </span>
                                );
                            })()}
                            {patientSuggest.length > 1 && (() => { const m = sameNameCountMap(patientRowsCacheRef.current.rows); return patientSuggest.some(x => sameNameCountOf(m, x.name) >= 2); })() && (
                                <span style={{ padding: '2px 8px', borderRadius: '999px', background: '#FFEDD5', color: '#9A3412', fontWeight: 700, fontSize: '12px' }}>동명이인 주의</span>
                            )}
                            {patientSuggest.length === 1 ? (
                                <button type="button" onClick={() => applyPatientSuggestion(patientSuggest[0])}
                                    style={{ padding: '5px 14px', border: 'none', borderRadius: '7px', background: '#4F46E5', color: '#fff', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>연결</button>
                            ) : (
                                <button type="button" onClick={() => { setShowPatientPicker(true); setPatientSuggest([]); }}
                                    style={{ padding: '5px 14px', border: 'none', borderRadius: '7px', background: '#4F46E5', color: '#fff', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>명부에서 고르기</button>
                            )}
                            <button type="button" onClick={() => { setPatientSuggestDismissed(patientName.trim()); setPatientSuggest([]); }}
                                style={{ padding: '5px 10px', border: '1px solid #C7D2FE', borderRadius: '7px', background: '#fff', color: '#64748B', fontSize: '12.5px', cursor: 'pointer' }}>아니오</button>
                        </div>
                    )}
                    {/* 통합 에이전트 스캔 연결 배너 — 스캔 수집 숨김(2026-08-08) 시 미노출 */}
                    {scanIntakeOn && !linkedScan && scanSuggestion && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '10px', padding: '10px 14px', marginBottom: '12px' }}>
                            <ScanLine size={16} color="#2563EB" />
                            <span style={{ fontSize: '13px', color: '#1D4ED8' }}>
                                <b>{scanSuggestion.patientNameGuess || scanSuggestion.caseLabel}</b> 스캔이 있습니다 (파일 {(scanSuggestion.files || []).length}개) — 의뢰서와 함께 기공소로 보낼까요?
                            </span>
                            <button type="button" onClick={() => { setLinkedScan(scanSuggestion); setScanSuggestion(null); }}
                                style={{ padding: '5px 14px', border: 'none', borderRadius: '7px', background: '#2563EB', color: '#fff', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>연결</button>
                            <button type="button" onClick={() => setScanSuggestion(null)}
                                style={{ padding: '5px 10px', border: '1px solid #BFDBFE', borderRadius: '7px', background: '#fff', color: '#64748B', fontSize: '12.5px', cursor: 'pointer' }}>무시</button>
                        </div>
                    )}
                    {scanIntakeOn && linkedScan && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            backgroundColor: '#eef2ff', border: '1px solid #c7d2fe',
                            borderRadius: 10, padding: '10px 14px', marginBottom: 12,
                        }}>
                            <ScanLine size={18} color="#6366f1" />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#3730a3' }}>
                                    스캔 데이터 연결됨 — {linkedScan.caseLabel || linkedScan.id}
                                    {linkedScan.scanner && linkedScan.scanner !== 'unknown' ? ` (${linkedScan.scanner})` : ''}
                                </div>
                                <div style={{ fontSize: 12, color: '#6366f1', marginTop: 2 }}>
                                    파일 {(linkedScan.files || []).length}개
                                    {(linkedScan.files || []).map(f => f.arch).filter(Boolean).join(' · ') ? ` — ${(linkedScan.files || []).map(f => f.arch).filter(Boolean).join(' · ')}` : ''}
                                    {linkedScan.status === 'detected' ? ' · 의뢰 제출 시 치과 PC에서 자동 전송됩니다' : ''}
                                </div>
                            </div>
                            <button
                                onClick={() => { if (linkedScan?.id) autoAppliedScanRef.current.delete(linkedScan.id); setLinkedScan(null); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                                title="스캔 연결 해제"
                            >
                                <X size={16} color="#6366f1" />
                            </button>
                        </div>
                    )}
                    {/* 🦷 스캔 주문서 자동 기입 안내 (2026-08-18 대표 지시 — 버튼 없이 연결 즉시 자동 적용됨) */}
                    {scanIntakeOn && linkedScan?.parsedOrder?.teethDetails?.length > 0 && (
                        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '10px 14px', marginBottom: '12px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: '#15803D', marginBottom: 4 }}>
                                ✅ 스캔 주문서 내용을 자동으로 입력했습니다 — 아래에서 확인만 해주세요 (브릿지 연결은 직접)
                            </div>
                            <div style={{ fontSize: '12.5px', color: '#166534' }}>
                                {linkedScan.parsedOrder.teethDetails.map(d =>
                                    `#${d.tooth} ${d.prosthesis}${d.material ? `(${d.material})` : ''}${d.shade ? ` ${d.shade}` : ''}`
                                ).join(' · ')}
                                {linkedScan.parsedOrder.dueDate ? ` · 납기 ${String(linkedScan.parsedOrder.dueDate).slice(0, 10)}` : ''}
                            </div>
                        </div>
                    )}

                    {/* 환자 / 거래 정보 */}
                    <div style={styles.sectionContainer}>
                        <div style={styles.sectionHeader}>
                            <div style={{ ...styles.sectionIconContainer, backgroundColor: '#6366f1', width: 32, height: 32, borderRadius: 8 }}>
                                <User size={15} color="#ffffff" />
                            </div>
                            <span style={styles.sectionTitle}>환자 / 거래 정보</span>
                            <span style={styles.requiredBadge}>필수</span>
                        </div>
                        <div style={{ padding: '12px 16px' }}>
                            {/* 1행: 환자명 + 성별 + 나이 */}
                            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                                <div style={{ flex: 2 }}>
                                    <label style={styles.compactLabel}>환자명</label>
                                    <div style={styles.compactInputContainer}>
                                        <User size={15} color="#9ca3af" />
                                        <input
                                            style={styles.compactInput}
                                            value={patientName}
                                            onChange={(e) => { setPatientName(e.target.value); if (linkedPatient && e.target.value !== linkedPatient.name) setLinkedPatient(null); if (linkedReception && e.target.value !== linkedReception.name) setLinkedReception(null); if (deskPickName && e.target.value.trim() !== deskPickName) setDeskPickName(''); }}
                                            placeholder="환자 이름"
                                        />
                                        {/* 환자관리 1단계: 명부에서 선택 (플래그·베타, 신규 작성만) */}
                                        {clinicPatientsOn && !editMode && (
                                            <button type="button" onClick={() => setShowPatientPicker(true)}
                                                style={{ flexShrink: 0, padding: '4px 8px', border: '1px solid #c7d2fe', borderRadius: 6, background: linkedPatient ? '#6366f1' : '#eef2ff', color: linkedPatient ? '#fff' : '#4338ca', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                {linkedPatient ? '명부 연결됨' : '명부에서 선택'}
                                            </button>
                                        )}
                                        {/* 🧾 오늘 접수 환자 선택 (플래그 deskReception, 신규 작성만) */}
                                        {deskReceptionOn && !editMode && (
                                            <button type="button" onClick={openReceptionPicker}
                                                style={{ flexShrink: 0, padding: '4px 8px', border: '1px solid #a7d3c8', borderRadius: 6, background: linkedReception ? '#0f6b5c' : '#e2f0ec', color: linkedReception ? '#fff' : '#0f6b5c', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                {linkedReception ? '접수 연결됨' : '오늘 접수'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={styles.compactLabel}>성별</label>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <button
                                            style={{ ...styles.compactGenderBtn, ...(patientGender === 'M' ? styles.genderButtonActiveM : {}) }}
                                            onClick={() => setPatientGender('M')}
                                        >남</button>
                                        <button
                                            style={{ ...styles.compactGenderBtn, ...(patientGender === 'F' ? styles.genderButtonActiveF : {}) }}
                                            onClick={() => setPatientGender('F')}
                                        >여</button>
                                    </div>
                                </div>
                                <div style={{ flex: 0.6 }}>
                                    <label style={styles.compactLabel}>나이</label>
                                    <div style={styles.compactInputContainer}>
                                        <input
                                            style={{ ...styles.compactInput, textAlign: 'center' }}
                                            value={patientAge}
                                            onChange={(e) => setPatientAge(e.target.value)}
                                            placeholder="00"
                                            type="number"
                                        />
                                        <span style={{ fontSize: 12, color: '#64748b' }}>세</span>
                                    </div>
                                </div>
                            </div>
                            {/* 2행: 거래처 + 납기일 */}
                            <div style={{ display: 'flex', gap: 10 }}>
                                <div style={{ flex: 1 }}>
                                    <label style={styles.compactLabel}>거래처</label>
                                    <button style={styles.compactSelectBox} onClick={() => setShowPartnerModal(true)}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                                            <Handshake size={15} color="#6366f1" />
                                            <span style={{ fontSize: 14, color: selectedPartnerName ? '#0f172a' : '#9ca3af', fontWeight: selectedPartnerName ? '500' : '400' }}>
                                                {selectedPartnerName || '거래처 선택'}
                                            </span>
                                        </div>
                                        <ChevronRight size={16} color="#9ca3af" />
                                    </button>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={styles.compactLabel}>납기일</label>
                                    <button style={styles.compactSelectBox} onClick={() => { setCalendarDate(dueDate); setShowDatePicker(true); }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                                            <Calendar size={15} color="#f59e0b" />
                                            <span style={{ fontSize: 14, color: '#0f172a', fontWeight: '500' }}>
                                                {dueDate.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' })}
                                            </span>
                                        </div>
                                        <span style={{ ...styles.dueDateBadge, fontSize: 11, padding: '2px 8px' }}>D-{dDayCount}</span>
                                    </button>
                                </div>
                                {/* 환자 내원일 (선택) — 대표 확정 2026-08-25 D2로 부활 (08-21 제거분 복원, 회의록_2026-08-25_에이전트_화면기준안).
                                    납기일 옆 [+ 내원일] 버튼 → 납기일+1일 자동 제안, 선택 시 "여유 N일" 표시. 비워도 발송 가능. */}
                                <div style={{ flex: 1 }}>
                                    <label style={styles.compactLabel}>{'환자 내원일 (선택)'}</label>
                                    {visitDate === null ? (
                                        <button
                                            type="button"
                                            style={{ ...styles.compactSelectBox, justifyContent: 'flex-start' }}
                                            onClick={() => {
                                                // 납기일 다음날을 기본 제안 (목업 확정 UX)
                                                const d = new Date(dueDate);
                                                d.setDate(d.getDate() + 1);
                                                setVisitDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
                                            }}
                                        >
                                            <span style={{ fontSize: 14, color: '#9ca3af' }}>{'+ 내원일 추가'}</span>
                                        </button>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <input
                                                type="date"
                                                value={visitDate}
                                                onChange={(e) => setVisitDate(e.target.value || null)}
                                                style={{ flex: 1, minWidth: 0, padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, color: '#0f172a', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                            />
                                            {(() => {
                                                // 납기 대비 여유일 — 내원일이 납기보다 빠르면 빨간 경고 (제작이 못 따라간다)
                                                const v = new Date(visitDate + 'T00:00:00');
                                                if (isNaN(v.getTime())) return null;
                                                const d0 = new Date(dueDate); d0.setHours(0, 0, 0, 0);
                                                const slack = Math.round((v.getTime() - d0.getTime()) / 86400000);
                                                return (
                                                    <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', color: slack < 0 ? '#dc2626' : slack === 0 ? '#d97706' : '#16a34a' }}>
                                                        {slack < 0 ? `납기보다 ${-slack}일 앞` : `→ 여유 ${slack}일`}
                                                    </span>
                                                );
                                            })()}
                                            <button
                                                type="button"
                                                onClick={() => setVisitDate(null)}
                                                title="내원일 지우기 (비워도 보낼 수 있습니다)"
                                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: '2px 4px', lineHeight: 1 }}
                                            >{'✕'}</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* 내원일 안내 문구 (목업 확정 카피) */}
                            <div style={{ marginTop: 6, fontSize: 11.5, color: '#94a3b8', lineHeight: 1.5 }}>
                                {'내원일: 예약을 잡을 때 이미 아는 날짜입니다. 넣으면 늦어질 때 미리 알려드립니다. 비워도 보낼 수 있습니다.'}
                            </div>
                        </div>
                    </div>

                    {/* 리메이크 작성으로 들어온 경우 — 이건 편집이 아니라 새 의뢰서임을 분명히 (2026-08-11) */}


                    {remakeFrom && (


                        <div style={{ margin: '0 16px 12px', padding: '11px 13px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13, lineHeight: 1.6 }}>


                            <span style={{ display: 'inline-block', marginRight: 6, padding: '1px 8px', borderRadius: 999, fontSize: 12, fontWeight: 800, background: remakeKind === 'repair' ? '#ecfeff' : '#fee2e2', color: remakeKind === 'repair' ? '#0e7490' : '#b91c1c', border: `1px solid ${remakeKind === 'repair' ? '#a5f3fc' : '#fecaca'}` }}>{remakeKind === 'repair' ? '수리' : '재제작'}</span>
                            <b>{remakeKind === 'repair' ? '수리' : '재제작'} 의뢰서를 만드는 중입니다</b> — {remakeFrom.patientName || '환자'}의 치식·재료·특이사항을 불러왔습니다 (사진과 납기는 새로 넣어주세요).


                            바뀐 부분만 고치고 <b>사유</b>를 골라 보내주세요. 원래 의뢰서는 그대로 남습니다.


                        </div>


                    )}


                    {/* 리메이크 / 수정 */}
                    <div style={styles.sectionContainer}>
                        <div style={styles.remakeHeader}>
                            <div style={styles.remakeHeaderLeft}>
                                <div style={{ ...styles.sectionIconContainer, backgroundColor: '#ef4444' }}>
                                    <RefreshCw size={18} color="#ffffff" />
                                </div>
                                <span style={styles.sectionTitle}>리메이크 / 수정</span>
                            </div>
                        </div>
                        <div style={{ padding: '0 16px 12px', display: 'flex', gap: 8 }}>
                            {[
                                { key: '', label: '해당없음', color: '#6b7280', bg: '#f3f4f6', activeBg: '#e2e8f0', activeBorder: '#94a3b8' },
                                { key: 'remake', label: '리메이크', color: '#dc2626', bg: '#fef2f2', activeBg: '#fee2e2', activeBorder: '#ef4444' },
                                { key: 'modify', label: '수정', color: '#d97706', bg: '#fffbeb', activeBg: '#fef3c7', activeBorder: '#f59e0b' },
                            ].map(opt => {
                                const selected = (isRemake ? remakeType : '') === opt.key || (!isRemake && opt.key === '');
                                return (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => {
                                            if (opt.key === '') {
                                                setIsRemake(false);
                                                setRemakeType('');
                                                setRemakeReason('');
                                                setRemakeCause('');
                                                setRemakeOf(null);
                                            } else {
                                                setIsRemake(true);
                                                setRemakeType(opt.key);
                                            }
                                        }}
                                        style={{
                                            flex: 1,
                                            padding: '10px 0',
                                            borderRadius: 10,
                                            border: `2px solid ${selected ? opt.activeBorder : '#e5e7eb'}`,
                                            backgroundColor: selected ? opt.activeBg : '#fff',
                                            color: selected ? opt.color : '#9ca3af',
                                            fontWeight: selected ? 700 : 500,
                                            fontSize: 14,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                        {isRemake && remakeType && (
                            <div style={styles.sectionContent}>
                                {/* 원 의뢰서 연결 (2026-08-09 대표 확정) — 처음 만들 때의 의뢰서를 지목해야
                                    "며칠 만에 다시 왔는지·몇 번째인지"를 정확히 셀 수 있다 */}
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>원 의뢰서 (처음 만들 때 보낸 의뢰서)</label>
                                    {remakeOf ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: '1.5px solid #10b981', borderRadius: 8, backgroundColor: '#ecfdf5' }}>
                                            <span style={{ flex: 1, fontSize: 13, color: '#065f46', fontWeight: 600 }}>{remakeOf.label || '선택된 의뢰서'}</span>
                                            <button type="button" onClick={() => setRemakeOf(null)}
                                                style={{ padding: '5px 10px', border: '1px solid #a7f3d0', borderRadius: 6, background: '#fff', color: '#065f46', fontSize: 12, cursor: 'pointer' }}>
                                                변경
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <input
                                                    style={{ ...styles.textInput, flex: 1, margin: 0 }}
                                                    placeholder="환자 이름으로 찾기"
                                                    defaultValue={patientName}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchOriginalOrders(e.target.value); } }}
                                                    onBlur={(e) => searchOriginalOrders(e.target.value)}
                                                />
                                                <button type="button" onClick={() => searchOriginalOrders(patientName)}
                                                    style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                                    찾기
                                                </button>
                                            </div>
                                            {remakeSearching && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>찾는 중…</div>}
                                            {!remakeSearching && remakeCandidates.length > 0 && (
                                                <div style={{ marginTop: 6, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                                                    {remakeCandidates.map(c => (
                                                        <button key={c.id} type="button"
                                                            onClick={() => { setRemakeOf({ id: c.id, date: c.date, label: c.label }); setRemakeCandidates([]); }}
                                                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', background: '#fff', fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                                                            {c.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {!remakeSearching && remakeCandidates.length === 0 && (
                                                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6, lineHeight: 1.6 }}>
                                                    최근 6개월 안에 이 환자 이름으로 보낸 의뢰서를 찾습니다. 처음 건이 덴클에 없으면 비워두셔도 됩니다.
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* 표준 사유 선택 (2026-08-09) — 자유 입력만으로는 사유별 분포 집계가 안 돼서 코드값을 함께 받는다 */}
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>사유 선택</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {REMAKE_CAUSES.map(c => {
                                            const on = remakeCause === c.code;
                                            return (
                                                <button
                                                    key={c.code}
                                                    type="button"
                                                    onClick={() => {
                                                        const next = on ? '' : c.code;
                                                        setRemakeCause(next);
                                                        // 자유 입력칸: 비어 있거나 앞서 자동으로 넣은 문구일 때만 바꾼다
                                                        const auto = Object.values(REMAKE_CAUSE_TEXT).filter(Boolean);
                                                        setRemakeReason(prev => {
                                                            const cur = (prev || '').trim();
                                                            if (cur && !auto.includes(cur)) return prev;   // 손으로 쓴 건 그대로
                                                            return next ? (REMAKE_CAUSE_TEXT[next] || '') : '';
                                                        });
                                                    }}
                                                    title={c.hint || ''}
                                                    style={{
                                                        padding: '8px 12px',
                                                        borderRadius: 8,
                                                        border: `1.5px solid ${on ? '#ef4444' : '#e5e7eb'}`,
                                                        backgroundColor: on ? '#fee2e2' : '#fff',
                                                        color: on ? '#b91c1c' : '#6b7280',
                                                        fontWeight: on ? 700 : 500,
                                                        fontSize: 13,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {c.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>
                                        {remakeType === 'remake' ? '리메이크 사유' : '수정 사유'}
                                    </label>
                                    <textarea
                                        style={styles.textAreaInput}
                                        value={remakeReason}
                                        onChange={(e) => setRemakeReason(e.target.value)}
                                        placeholder={remakeType === 'remake' ? '위에서 사유를 고르면 자동으로 채워집니다 · 더 적을 내용이 있으면 여기에' : '위에서 사유를 고르면 자동으로 채워집니다 · 더 적을 내용이 있으면 여기에'}
                                        rows={4}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 보철물 설정 - 통합 컴포넌트 */}
                    <ProsthesisSelector
                        ref={selectorRef}
                        key={(draftLoaded ? 'ready' : 'init') + '-' + selectorEpoch}   // 편집/임시저장 복원 완료 후 리마운트 (2026-07-19) + 스캔 [의뢰서에 적용] 자동 채움 시 리마운트 (2026-08-18)
                        prosthesisGroups={prosthesisGroups}
                        setProsthesisGroups={setProsthesisGroups}
                        bridgeGroups={bridgeGroups}
                        setBridgeGroups={setBridgeGroups}
                        ponticGroups={ponticGroups}
                        setPonticGroups={setPonticGroups}
                        implantGroups={implantGroups}
                        setImplantGroups={setImplantGroups}
                        teethDetails={teethDetails}
                        setTeethDetails={setTeethDetails}
                        selectedTeeth={selectedTeeth}
                        setSelectedTeeth={setSelectedTeeth}
                        prosthesisOptions={prosthesisOptions}
                        prosthesisMaterialMap={prosthesisMaterialMap}
                        customProsthesisOptions={customProsthesisOptions}
                        customMaterialMap={customMaterialMap}
                        implantOptions={[...implantOptions, ...customImplantOptions]}
                        onAddProsthesis={handleAddProsthesis}
                        onDeleteProsthesis={handleDeleteProsthesis}
                        onAddImplant={handleAddImplant}
                        onDeleteImplant={handleDeleteImplant}
                        implantCombos={customImplantCombos}
                        initialDecided={!!editMode || !!remakeFrom}   // 수정·리메이크는 원래 의뢰서에서 이미 정한 것
                        recentCombos={recentImplantCombos}
                        onAddImplantCombo={handleAddImplantCombo}
                        onDeleteImplantCombo={handleDeleteImplantCombo}
                        storageKey={targetUserId || ''}
                    />
                    {comboSaveNotice && (
                        <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', marginTop: 6 }}>
                            {comboSaveNotice}
                        </div>
                    )}

                    {/* Shade 설정 */}
                    {selectedTeeth.length > 0 && (
                        <div style={styles.sectionContainer}>
                            <div style={styles.sectionHeader}>
                                <div style={{ ...styles.sectionIconContainer, backgroundColor: '#ec4899' }}>
                                    <Palette size={18} color="#ffffff" />
                                </div>
                                <span style={styles.sectionTitle}>Shade 설정</span>
                            </div>
                            <div style={styles.sectionContent}>
                                <p style={styles.shadeDesc}>선택된 모든 치아({selectedTeeth.length}개)에 적용됩니다</p>
                                <div style={styles.shadeTypeRow}>
                                    <button
                                        style={{ ...styles.shadeTypeButton, ...(shadeType === 'classic' ? styles.shadeTypeButtonActive : {}) }}
                                        onClick={() => setShadeType('classic')}
                                    >
                                        Vita Classic
                                    </button>
                                    <button
                                        style={{ ...styles.shadeTypeButton, ...(shadeType === '3d-master' ? styles.shadeTypeButtonActive : {}) }}
                                        onClick={() => setShadeType('3d-master')}
                                    >
                                        3D Master
                                    </button>
                                </div>
                                <div style={styles.shadeRow}>
                                    {shadeOptions[shadeType].map(shade => (
                                        <button
                                            key={shade}
                                            style={{ ...styles.shadeButton, ...(selectedShade === shade ? styles.shadeButtonActive : {}) }}
                                            onClick={() => setSelectedShade(shade)}
                                        >
                                            {shade}
                                        </button>
                                    ))}
                                </div>
                                {selectedShade && (
                                    <div style={styles.shadeSelected}>
                                        <CheckCircle size={18} color="#10b981" />
                                        <span>선택된 Shade: {selectedShade}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 이미지 */}
                    <div style={styles.sectionContainer}>
                        <div style={{ ...styles.sectionHeader, padding: '12px 16px' }}>
                            <div style={{ ...styles.sectionIconContainer, backgroundColor: '#06b6d4', width: 32, height: 32, borderRadius: 8 }}>
                                <Image size={15} color="#ffffff" />
                            </div>
                            <span style={styles.sectionTitle}>이미지</span>
                        </div>
                        <div style={{ padding: '10px 16px' }}>
                            {/* 첨부 통합 (2026-08-21 대표 지시): 버튼 1개 — 사진은 썸네일, 그 외 파일은 목록으로 자동 분류 */}
                            <div style={{ display: 'flex', gap: 8 }}>
                                <label style={{ ...styles.imageActionButton, padding: 10, fontSize: 13, borderRadius: 8, flex: 1, justifyContent: 'center' }}>
                                    <Paperclip size={18} color="#6366f1" />
                                    <span>사진·파일 첨부</span>
                                    <input type="file" multiple onChange={handleAttachUnified} style={{ display: 'none' }} />
                                </label>
                            </div>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>사진·문서·zip 무엇이든 선택하세요 (휴대폰은 카메라/앨범 선택창이 뜹니다) · 폴더째는 압축(zip) 첨부 · 파일당 200MB까지</div>
                            {attachFiles.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    {attachFiles.map((f, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, color: '#334155', padding: '5px 2px', borderBottom: '1px solid #f1f5f9' }}>
                                            <span style={{ flex: 1, wordBreak: 'break-all' }}>📎 {f.name} <span style={{ color: '#94a3b8' }}>({(f.size / 1048576).toFixed(1)}MB)</span></span>
                                            <button type="button" onClick={() => setAttachFiles(prev => prev.filter((_, j) => j !== i))} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}>삭제</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div style={{ display: 'none' }}>
                            </div>
                            {images.length > 0 && (
                                <div style={{ ...styles.imageGrid, marginTop: 8 }}>
                                    {images.map((image, index) => (
                                        <div key={index} style={{ width: 64, height: 64, position: 'relative' }}>
                                            <img src={image.uri} alt="" style={styles.imageThumb} />
                                            <button style={styles.imageRemove} onClick={() => removeImage(index)}>
                                                <X size={12} color="#fff" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 특이사항 */}
                    <div style={styles.sectionContainer}>
                        <div style={{ ...styles.sectionHeader, padding: '12px 16px' }}>
                            <div style={{ ...styles.sectionIconContainer, backgroundColor: '#f59e0b', width: 32, height: 32, borderRadius: 8 }}>
                                <FileText size={15} color="#ffffff" />
                            </div>
                            <span style={styles.sectionTitle}>특이사항</span>
                        </div>
                        <div style={{ padding: '10px 16px' }}>
                            <textarea
                                style={{ ...styles.textAreaInput, minHeight: 70, padding: 10, fontSize: 13 }}
                                value={specialNotes}
                                onChange={(e) => setSpecialNotes(e.target.value)}
                                placeholder="특이사항을 입력하세요..."
                                rows={3}
                            />
                            <div style={{ marginTop: 8, padding: 10, backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: specialNotesShortcuts.length > 0 ? 8 : 0 }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>빠른입력</span>
                                    <button onClick={() => setShowShortcutModal(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#6366f1' }}>관리</button>
                                </div>
                                {specialNotesShortcuts.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {specialNotesShortcuts.map((shortcut, index) => (
                                            <button
                                                key={index}
                                                style={{ padding: '5px 12px', backgroundColor: '#eef2ff', borderRadius: 16, border: 'none', fontSize: 12, fontWeight: 600, color: '#6366f1', cursor: 'pointer' }}
                                                onClick={() => setSpecialNotes(prev => prev ? `${prev}\n${shortcut}` : shortcut)}
                                            >
                                                {shortcut}
                                            </button>
                                        ))}
                                        {specialNotes.trim() && !specialNotesShortcuts.includes(specialNotes.trim()) && (
                                            <button
                                                style={{ padding: '5px 12px', backgroundColor: '#fff', borderRadius: 16, border: '1px dashed #6366f1', fontSize: 12, fontWeight: 600, color: '#6366f1', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                                onClick={async () => {
                                                    const s = specialNotes.trim();
                                                    if (!s) return;
                                                    const updated = [...specialNotesShortcuts, s];
                                                    setSpecialNotesShortcuts(updated);
                                                    try {
                                                        const targetId = user.companyId || user.uid;
                                                        await updateDoc(doc(db, 'users', targetId), { specialNotesShortcuts: updated });
                                                    } catch (e) { console.error('저장 실패:', e); }
                                                }}
                                            >
                                                <Plus size={11} />
                                                "{specialNotes.trim().length > 10 ? specialNotes.trim().substring(0, 10) + '...' : specialNotes.trim()}" 저장
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>자주 쓰는 문구를 저장할 수 있습니다</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 「디지털 파일 (선택)」 섹션 제거 — 대표 지시 2026-08-21 "파일 올리는 곳은 하나".
                        STL·zip도 위 [사진·파일 첨부]로 통합. 기존 저장분 표시(ViewOrder)는 유지. */}

                    {/* 발송 전 확인 박스 (2026-09-29): 임플란트 브랜드만 있고 보철 종류가 없는 치아 */}
                    {impOnlyWarn && impOnlyWarn.teeth.length > 0 && (
                        <div ref={impOnlyWarnRef} style={styles.impOnlyWarnBox}>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#92400e', lineHeight: 1.5 }}>
                                {'#' + impOnlyWarn.teeth.join(', #') + ' — 임플란트 브랜드만 있고 보철 종류(크라운)가 없습니다. 크라운 없이 보내는 게 맞나요?'}
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button type="button" style={styles.impOnlyWarnBtnPrimary}
                                    onClick={() => {
                                        setImpOnlyWarn(null);
                                        if (selectorRef.current && selectorRef.current.activateNatChip) selectorRef.current.activateNatChip();
                                    }}>
                                    크라운 붙이러 가기
                                </button>
                                <button type="button" style={styles.impOnlyWarnBtn} disabled={uploading}
                                    onClick={() => { setImpOnlyWarn(null); handleSubmit({ skipImplantCheck: true }); }}>
                                    이대로 보내기
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 제출 버튼 */}
                    <button
                        style={{ ...styles.submitButton, ...(uploading ? styles.submitButtonDisabled : {}) }}
                        onClick={handleSubmit}
                        disabled={uploading}
                    >
                        <CheckCircle size={20} color="#ffffff" />
                        <span>{uploading ? (editMode ? '수정 중...' : '생성 중...') : (editMode ? '주문 수정' : '주문 생성')}</span>
                    </button>
                </div>
            </div>

            {/* 날짜 선택 달력 모달 */}
            {showDatePicker && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                        <div style={styles.modalHeader}>
                            <span style={styles.modalTitle}>납기일 선택</span>
                            <button onClick={() => setShowDatePicker(false)} style={styles.modalCloseBtn}>
                                <X size={24} color="#64748b" />
                            </button>
                        </div>
                        <div style={styles.modalContent}>
                            <div style={styles.calendarNav}>
                                <button style={styles.calendarNavBtn} onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}>
                                    <ChevronLeft size={20} />
                                </button>
                                <span style={styles.calendarMonth}>
                                    {calendarDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })}
                                </span>
                                <button style={styles.calendarNavBtn} onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}>
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                            <div style={styles.calendarWeekHeader}>
                                {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
                                    <span key={d} style={{ ...styles.calendarWeekDay, color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : '#64748b' }}>{d}</span>
                                ))}
                            </div>
                            <div style={styles.calendarGrid}>
                                {getCalendarDays().map((date, idx) => (
                                    <button
                                        key={idx}
                                        style={{
                                            ...styles.calendarDay,
                                            ...(date && isSameDay(date, dueDate) ? styles.calendarDaySelected : {}),
                                            ...(isDateDisabled(date) ? styles.calendarDayDisabled : {}),
                                        }}
                                        onClick={() => { if (date && !isDateDisabled(date)) { setDueDate(date); setShowDatePicker(false); } }}
                                        disabled={isDateDisabled(date)}
                                    >
                                        {date ? date.getDate() : ''}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 거래처 모달 */}
            {showPartnerModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                        <div style={styles.modalHeader}>
                            <span style={styles.modalTitle}>거래처 선택</span>
                            <button onClick={() => setShowPartnerModal(false)} style={styles.modalCloseBtn}>
                                <X size={24} color="#64748b" />
                            </button>
                        </div>
                        <div style={styles.modalContent}>
                            {/* 내 기공실 옵션 */}
                            <button
                                style={{ ...styles.partnerItem, ...(isInternalLab ? styles.partnerItemActive : {}), borderBottom: "2px solid #e2e8f0", marginBottom: 8 }}
                                onClick={() => { setIsInternalLab(true); setSelectedPartnerId(targetUserId); setShowPartnerModal(false); }}
                            >
                                <div style={{ ...styles.partnerAvatar, backgroundColor: "#10b981" }}>🏠</div>
                                <span style={styles.partnerName}>{internalLabName}</span>
                                {isInternalLab && <CheckCircle size={22} color="#6366f1" />}
                            </button>
                            {/* 외부 거래처 */}
                            {partners.length > 0 ? partners.map(partner => (
                                <button
                                    key={partner.id}
                                    style={{ ...styles.partnerItem, ...(selectedPartnerId === partner.id ? styles.partnerItemActive : {}) }}
                                    onClick={() => { setIsInternalLab(false); setSelectedPartnerId(partner.id); setShowPartnerModal(false); }}
                                >
                                    <div style={styles.partnerAvatar}>{partner.name[0]}</div>
                                    <span style={styles.partnerName}>{partner.name}</span>
                                    {selectedPartnerId === partner.id && <CheckCircle size={22} color="#6366f1" />}
                                </button>
                            )) : (
                                <div style={styles.emptyState}>연결된 거래처가 없습니다</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 단축어 모달 */}
            {showShortcutModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                        <div style={styles.modalHeader}>
                            <span style={styles.modalTitle}>빠른입력 관리</span>
                            <button onClick={() => { setShowShortcutModal(false); setNewShortcut(''); }} style={styles.modalCloseBtn}>
                                <X size={24} color="#64748b" />
                            </button>
                        </div>
                        <div style={styles.modalContent}>
                            <div style={styles.addShortcutRow}>
                                <input
                                    style={styles.addShortcutInput}
                                    value={newShortcut}
                                    onChange={(e) => setNewShortcut(e.target.value)}
                                    placeholder="새 빠른입력 문구"
                                />
                                <button style={styles.addShortcutBtn} onClick={addShortcut}>
                                    <Plus size={20} color="#fff" />
                                </button>
                            </div>
                            {specialNotesShortcuts.map((s, i) => (
                                <div key={i} style={styles.shortcutListItem}>
                                    <span>{s}</span>
                                    <button onClick={() => removeShortcut(i)} style={styles.shortcutDeleteBtn}>
                                        <Trash2 size={18} color="#ef4444" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f1f5f9' },

    // 헤더
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0' },
    headerButton: { width: 40, height: 40, display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer' },
    headerTitle: { fontSize: 20, fontWeight: '700', color: '#0f172a' },

    scrollView: { flex: 1, overflowY: 'auto' },
    contentWrapper: { maxWidth: 900, margin: '0 auto', padding: 24, width: '100%', boxSizing: 'border-box' },

    // 섹션
    sectionContainer: { backgroundColor: '#ffffff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 18 },
    sectionHeader: { display: 'flex', alignItems: 'center', padding: 18, borderBottom: '1px solid #f1f5f9', gap: 10 },
    sectionIconContainer: { width: 40, height: 40, borderRadius: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' },
    sectionTitle: { fontSize: 17, fontWeight: '700', color: '#0f172a', flex: 1 },
    requiredBadge: { fontSize: 12, fontWeight: '600', color: '#ef4444', backgroundColor: '#fef2f2', padding: '4px 10px', borderRadius: 6 },
    sectionContent: { padding: 20 },

    // 입력
    inputGroup: { marginBottom: 18 },
    inputLabel: { display: 'block', fontSize: 14, fontWeight: '600', color: '#64748b', marginBottom: 6 },
    modernInputContainer: { display: 'flex', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', height: 52, gap: 8 },
    modernInput: { flex: 1, fontSize: 16, color: '#0f172a', border: 'none', background: 'none', outline: 'none' },

    rowInputs: { display: 'flex', gap: 10 },
    genderInputGroup: { flex: 1.3 },
    ageInputGroup: { flex: 0.7 },
    genderButtons: { display: 'flex', gap: 8 },
    genderButton: { flex: 1, height: 54, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 15, fontWeight: '600', color: '#64748b', cursor: 'pointer' },
    genderButtonActiveM: { backgroundColor: '#3b82f6', borderColor: '#3b82f6', color: '#ffffff' },
    genderButtonActiveF: { backgroundColor: '#ec4899', borderColor: '#ec4899', color: '#ffffff' },
    ageInputContainer: { display: 'flex', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', height: 46 },
    ageInput: { flex: 1, fontSize: 16, color: '#0f172a', textAlign: 'center', border: 'none', background: 'none', outline: 'none' },
    ageSuffix: { fontSize: 13, color: '#64748b', fontWeight: '500' },

    compactLabel: { display: 'block', fontSize: 12, fontWeight: '600', color: '#64748b', marginBottom: 4 },
    compactInputContainer: { display: 'flex', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', padding: '0 10px', height: 40, gap: 6 },
    compactInput: { flex: 1, fontSize: 14, color: '#0f172a', border: 'none', background: 'none', outline: 'none' },
    compactGenderBtn: { flex: 1, height: 40, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: '600', color: '#64748b', cursor: 'pointer' },
    compactSelectBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', padding: '0 10px', height: 40, cursor: 'pointer' },
    selectBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', backgroundColor: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px', height: 56, cursor: 'pointer' },
    selectBoxLeft: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 },
    selectBoxText: { fontSize: 16, color: '#0f172a', fontWeight: '500' },
    selectBoxPlaceholder: { fontSize: 16, color: '#9ca3af', fontWeight: '500' },
    dueDateBadge: { backgroundColor: '#fef3c7', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: '700', color: '#d97706' },

    // 리메이크
    remakeHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'none', border: 'none', width: '100%', cursor: 'pointer' },
    remakeHeaderLeft: { display: 'flex', alignItems: 'center', gap: 10 },
    toggleSwitch: { width: 48, height: 26, borderRadius: 13, backgroundColor: '#e2e8f0', padding: 2, display: 'flex', alignItems: 'center' },
    toggleSwitchActive: { backgroundColor: '#6366f1' },
    toggleKnob: { width: 22, height: 22, borderRadius: 11, backgroundColor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'margin-left 0.2s' },
    toggleKnobActive: { marginLeft: 22 },

    textAreaInput: { width: '100%', minHeight: 100, padding: 12, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none' },

    // Shade
    shadeDesc: { fontSize: 12, color: '#64748b', marginBottom: 8 },
    shadeTypeRow: { display: 'flex', gap: 6, marginBottom: 8 },
    shadeTypeButton: { flex: 1, height: 36, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: '600', color: '#64748b', cursor: 'pointer' },
    shadeTypeButtonActive: { backgroundColor: '#6366f1', borderColor: '#6366f1', color: '#ffffff' },
    shadeRow: { display: 'flex', gap: 5, flexWrap: 'wrap', padding: '2px 0' },
    shadeButton: { padding: '7px 14px', backgroundColor: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: '600', color: '#374151', cursor: 'pointer' },
    shadeButtonActive: { backgroundColor: '#6366f1', borderColor: '#6366f1', color: '#ffffff' },
    shadeSelected: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, padding: 8, backgroundColor: '#f0fdf4', borderRadius: 6, fontSize: 13, fontWeight: '600', color: '#15803d' },

    // 이미지
    imageButtonRow: { display: 'flex', gap: 10 },
    imageActionButton: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 14, backgroundColor: '#eef2ff', borderRadius: 10, fontSize: 15, fontWeight: '600', color: '#6366f1', cursor: 'pointer' },
    imageGrid: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 },
    imageItem: { width: 80, height: 80, position: 'relative' },
    imageThumb: { width: '100%', height: '100%', borderRadius: 8, objectFit: 'cover' },
    imageRemove: { position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 10, width: 20, height: 20, display: 'flex', justifyContent: 'center', alignItems: 'center', border: 'none', cursor: 'pointer' },

    // 단축어
    shortcutLabel: { fontSize: 12, fontWeight: '600', color: '#64748b', marginBottom: 8 },
    shortcutRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
    shortcutChip: { display: 'flex', alignItems: 'center', gap: 4, padding: '8px 12px', backgroundColor: '#eef2ff', borderRadius: 20, border: 'none', fontSize: 12, fontWeight: '500', color: '#4f46e5', cursor: 'pointer' },
    editShortcutBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4 },

    // 제출 버튼
    impOnlyWarnBox: { display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px', borderRadius: 12, border: '1px solid #f59e0b', backgroundColor: '#fffbeb', marginTop: 8 },
    impOnlyWarnBtnPrimary: { padding: '8px 14px', borderRadius: 8, border: '1px solid #b45309', backgroundColor: '#b45309', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    impOnlyWarnBtn: { padding: '8px 14px', borderRadius: 8, border: '1px solid #b45309', backgroundColor: '#fff', color: '#b45309', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    submitButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '15px 0', backgroundColor: '#6366f1', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: '700', color: '#ffffff', cursor: 'pointer', marginTop: 8 },
    submitButtonDisabled: { backgroundColor: '#cbd5e1' },

    // 모달
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modal: { backgroundColor: '#fff', borderRadius: 16, width: '90%', maxWidth: 400, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 18, borderBottom: '1px solid #f1f5f9' },
    modalTitle: { fontSize: 17, fontWeight: '700', color: '#0f172a' },
    modalCloseBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4 },
    modalContent: { padding: 18, overflowY: 'auto' },

    // 달력
    calendarNav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    calendarNavBtn: { width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#64748b' },
    calendarMonth: { fontSize: 16, fontWeight: '700', color: '#0f172a' },
    calendarWeekHeader: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 8 },
    calendarWeekDay: { textAlign: 'center', fontSize: 12, fontWeight: '600', padding: '8px 0' },
    calendarGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 },
    calendarDay: { aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: '500', color: '#374151', cursor: 'pointer' },
    calendarDaySelected: { backgroundColor: '#6366f1', color: '#ffffff' },
    calendarDayDisabled: { backgroundColor: 'transparent', color: '#cbd5e1', cursor: 'default' },

    // 거래처
    partnerItem: { display: 'flex', alignItems: 'center', gap: 12, padding: 14, borderRadius: 10, marginBottom: 8, backgroundColor: '#f8fafc', border: 'none', width: '100%', cursor: 'pointer' },
    partnerItemActive: { backgroundColor: '#eef2ff', border: '1px solid #6366f1' },
    partnerAvatar: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#6366f1', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: 16, fontWeight: '700', color: '#ffffff' },
    partnerName: { flex: 1, fontSize: 14, fontWeight: '600', color: '#0f172a', textAlign: 'left' },
    emptyState: { textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 },

    // 단축어 관리
    addShortcutRow: { display: 'flex', gap: 8, marginBottom: 16 },
    addShortcutInput: { flex: 1, height: 44, backgroundColor: '#f8fafc', borderRadius: 8, padding: '0 12px', fontSize: 14, border: '1px solid #e2e8f0', outline: 'none' },
    addShortcutBtn: { width: 44, height: 44, backgroundColor: '#6366f1', borderRadius: 8, border: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' },
    shortcutListItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, backgroundColor: '#f8fafc', borderRadius: 8, marginBottom: 8, fontSize: 14, color: '#0f172a', fontWeight: '500' },
    shortcutDeleteBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4 },
};

export default CreateOrder;